/* ============================================================
   Potencium — storefront behaviour
   - Announcement bar rotation
   - Mobile hamburger toggle
   - AJAX add-to-cart with cart-count badge sync
   Memory hygiene: every controller registers listeners with an
   AbortController signal and is torn down on shopify:section:unload,
   re-initialised on shopify:section:load.
   ============================================================ */
(function () {
  "use strict";

  var controllers = []; // active teardowns, keyed by root element

  /* ---------- webview hardening (2026-08-10) ----------
     `new AbortController()` is the newest platform feature this file depends on, and it is
     used by 9 of the initialisers below. assets/pt-vxg-scroll.js was already hardened against
     exactly this (see its 2026-08-09 note: AbortController and the {signal} listener
     descriptor are "absent/rejected on older FB/IG in-app webview engines") — custom.js never
     was, and this is the store's only global script.

     Why it matters here even though boot() try/catches each init: if the constructor throws,
     it throws for EVERY init, so all 14 fail together and the storefront loses AJAX add-to-cart,
     the qty stepper, the Vitals↔qty sync and the Releasit slot relocation in one go. The
     purchase path itself survives (the PDP ATC is a real type="submit" inside a /cart/add form,
     and installCheckoutFirewall() uses plain listeners), so this is a degradation, not a dead
     page — but it is a large, avoidable one on 66% of traffic.

     Fallback semantics: listeners still bind, they just stop being removable — teardown
     becomes a no-op. That is safe here because boot() already guards against double-binding
     via el.__ptBound, so a re-boot cannot stack duplicate handlers. Degraded teardown beats
     no behaviour at all. */
  function makeAC() {
    try {
      return new AbortController();
    } catch (e) {
      /* An options object carrying signal:undefined is treated as "signal absent" by the
         dictionary conversion, so every existing { signal: signal } call site stays valid. */
      return { signal: undefined, abort: function () {} };
    }
  }

  /* ---------- helpers ---------- */
  function formatCount(n) { return n > 99 ? "99+" : String(n); }

  function paintCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = formatCount(count);
      el.hidden = count <= 0;
    });
  }

  function refreshCart(options) {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        if (!r.ok) throw new Error("cart.js HTTP " + r.status);
        return r.json();
      })
      .then(function (cart) {
        paintCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent("pt:cart:updated", { detail: cart }));
        return cart;
      })
      .catch(function (err) {
        if (options && options.strict) throw err;
        /* Ordinary badge refreshes are best-effort. Purchase bridges opt into strict mode. */
      });
  }


  /* 2026-08-30: the cart-level free-gift engine was removed along with the offer it
     served. Vitals no longer grants a tier-3 gift, so the engine had nothing to
     reconcile and its public entry point resolved false on every call. Its three call
     sites in the purchase path were collapsed onto that constant result rather than
     deleted blind, so add-to-cart and the COD form behave exactly as they did the
     moment before this removal. */

  /* ---------- Vitals-owned ATC + same-page Releasit COD form (2026-08-23) ----------
     PROBLEM SOLVED HERE
     Vitals owns the volume-discount tiers and claims the add-to-cart at document level
     (`document.addEventListener("click"|"submit", …, true)` in its storefront bundle,
     matched against the ATC form/button it discovered — here `form[action*="/cart/add"]`,
     i.e. our #pt-pdp-form). When Vitals takes that click over it calls
     `preventDefault()+stopPropagation()`, adds the tier's quantity itself, and then decides
     where to send the shopper. With no theme hooks installed that decision is "navigate to
     /cart" — exactly the redirect the owner does not want on the PDP.

     Vitals exposes merchant hooks through `vitalsSet(name, fn)`, and that setter is handed
     to the two functions the app-embed defines on `window.vtlsLiquidData`
     (`dynamicFunctions`, `documentInjectors` — read from the live bundle 2026-08-23,
     module 13467: `{...hR(), vitalsSet, vitalsGet, $}`). Wrapping either of them lets the
     THEME install hooks without touching the app's Custom-JS field:

       VITALS_HOOK__ON_ATC_STAY_ON_THE_SAME_PAGE  → called INSTEAD of Vitals' post-add
                                                    navigation (bundle: z67).
       VITALS_HOOK__OPEN_CART_DRAWER              → consulted before Vitals falls through
                                                    to "go to cart"; a truthy return
                                                    cancels that navigation (bundle: E72).

     Both land in handlePostAdd(), which refreshes /cart.js (the line and Vitals' discount
     are already in it) and — when the shopper's intent was "buy", not "add" — opens the
     Releasit COD form ON THE CURRENT PAGE through Releasit's own cart-mode API. Net effect:
     the shopper stays on the PDP and the COD form opens there instead of bouncing them to
     /cart. (2026-08-30: this block used to describe a tier-3 free gift that Vitals claimed
     the same way. That offer was withdrawn; only the tier discount remains.) */

  var PT_INTENT_TTL = 20000;      // a stale intent must never hijack a later, unrelated add
  var ptIntent = null;
  var ptIntentAt = 0;

  function setBuyIntent(kind) { ptIntent = kind; ptIntentAt = Date.now(); }
  function takeBuyIntent() {
    var v = (ptIntent && Date.now() - ptIntentAt <= PT_INTENT_TTL) ? ptIntent : null;
    ptIntent = null;
    return v;
  }

  function cartVariantQty(cart, id) {
    if (!cart || !cart.items || id == null) return 0;
    return cart.items.reduce(function (sum, item) {
      return sum + (Number(item.id) === Number(id) ? Number(item.quantity || 0) : 0);
    }, 0);
  }

  function releasitApi() {
    var api = window._rsiV2;
    if (!api || typeof api.getState !== "function" || typeof api.setState !== "function") return null;
    if (!api.cartService || typeof api.cartService.getCart !== "function") return null;
    return api;
  }
  function releasitFormIsOpen() {
    var api = releasitApi();
    if (!api) return false;
    try { var s = api.getState(); return !!(s && s.isOpen); } catch (e) { return false; }
  }
  /* Releasit's public opener, used as a last resort when the cart-mode handoff fails. */
  function releasitOpenViaApi() {
    if (window.ReleasitCOD && typeof window.ReleasitCOD.openForm === "function") { window.ReleasitCOD.openForm(); return true; }
    if (window.releasit && typeof window.releasit.openForm === "function") { window.releasit.openForm(); return true; }
    return false;
  }
  function ptWaitFor(probe, timeout, step) {
    return new Promise(function (resolve, reject) {
      var got = probe();
      if (got) return resolve(got);
      var waited = 0;
      var iv = setInterval(function () {
        var hit = probe();
        waited += step;
        if (hit) { clearInterval(iv); resolve(hit); }
        else if (waited >= timeout) { clearInterval(iv); reject(new Error("timed out waiting for dependency")); }
      }, step);
    });
  }
  function ptDelay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  /* Open the app-owned COD form over the CURRENT page with the confirmed cart.

     THIS IS THE CART PAGE'S OWN ROUTINE, RUN ON THE PDP.
     Read from Releasit's bundle (releasit-cod-form-441, 2026-08-23): on /cart it addresses
     the REAL Shopify cart directly, deriving the Storefront id from the AJAX cart token —
     `Tt = "gid://shopify/Cart/" + (await /cart.js).token` → `CartService.getCart(Tt)` — and
     drops that cart straight into its state. That is why the form there always carries the
     whole cart exactly as Vitals left it: it reads the same cart Vitals wrote to.
     We do exactly that here, so the PDP form is fed from the identical source as /cart.

     WHY THIS IS A READ, NOT A REBUILD (2026-08-23 fix).
     The first version copied Releasit's OTHER routine, `refreshCartFromCartJs`, which does
     clearCart(cartId) → addProduct(cartId, lines). That routine exists for the cart-DRAWER
     case, where Releasit works out of its own scratch cart and has to re-fill it. Running it
     here was wrong twice over: it rewrites a cart it does not own, and — because the click
     bridge and the Vitals hook can both reach this function for a single tap — two clears
     could overlap on the same cart, the second finding the line ids already gone. Shopify
     returns those as `cartLinesRemove.userErrors`, which Releasit surfaces as the toast
     "Error removing cart lines." A plain read cannot collide, and cannot lose a line.

     The clear+rebuild path is kept only for the case Releasit itself guards against: a cart
     token with no "?key=" signature, which cannot be addressed as a Storefront cart at all.

     Single-flight + idempotent: one tap can legitimately arrive here twice (bridge + hook);
     the second caller joins the first instead of starting a competing handoff. */
  var ptFormOpenInFlight = null;

  /* 2026-09-06 (P7): honour Releasit's "Buy only current product" setting
     (productPage.addMode = "product-only" - the mode Hungary runs on). The theme bridge
     bypasses Releasit's own PDP handler, so without this branch the form kept showing the
     WHOLE Shopify cart whatever the app setting said. In product-only mode the form is fed
     from a fresh Releasit scratch cart holding just the variant that was tapped, at the
     quantity Vitals/the theme put in the Shopify cart. The Vitals tier price is an automatic
     app discount, so Shopify applies it to that cart too - exactly how Hungary works
     natively. The shopper's real Shopify cart is NOT cleared or touched (in cart mode
     Releasit's cartInfo.id IS the real cart, so clearCart there would wipe it); other items
     simply are not part of THIS order. `?pt_addmode=product-only` in the URL forces the
     branch for preview testing because the app setting is store-wide. Any failure falls
     back to the cart-mode path below. */
  var ptBuyVariantId = null;
  function ptAddMode() {
    try {
      var q = /[?&]pt_addmode=([\w-]+)/.exec(window.location.search);
      if (q) return q[1];
      var s = window._RSI_COD_FORM_SETTINGS;
      return (s && s.productPage && s.productPage.addMode) || "";
    } catch (e) { return ""; }
  }
  function ptCartFromResult(r, key) {
    if (!r) return null;
    var d = r.data || r;
    return (d[key] && d[key].cart) || d.cart || null;
  }
  function ptOpenProductOnly(api, cart) {
    var id = ptBuyVariantId;
    if (!id || !cart || !cart.items) return null;
    var mine = cart.items.filter(function (it) { return Number(it.id) === Number(id); });
    if (!mine.length || typeof api.cartService.createCart !== "function" || typeof api.cartService.addProduct !== "function") return null;
    var lines = ptStorefrontLines({ items: mine });
    return Promise.resolve(api.cartService.createCart())
      .then(function (created) {
        var fresh = ptCartFromResult(created, "cartCreate");
        if (!fresh || !fresh.id) throw new Error("Releasit could not create a scratch cart");
        return api.cartService.addProduct(fresh.id, lines);
      })
      .then(function (result) {
        var cartInfo = ptCartFromResult(result, "cartLinesAdd");
        if (!cartInfo) throw new Error("Releasit scratch cart came back empty");
        return ptApplyCartToForm(api, cartInfo);
      });
  }

  function ptCartIdFromToken(cart) {
    /* Shopify's Storefront cart id is derived from the AJAX cart token, but ONLY once the
       token carries its "?key=" signature. Releasit calls /cart/clear.js to force a fresh
       token when it does not — we must never do that: clearing the cart would throw away the
       Vitals-written lines we are here to preserve. */
    var token = cart && cart.token;
    if (!token || String(token).indexOf("?key=") === -1) return null;
    return "gid://shopify/Cart/" + token;
  }
  function ptStorefrontLines(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map(function (item) {
      var line = {
        merchandiseId: "gid://shopify/ProductVariant/" + item.id,
        quantity: Number(item.quantity) || 1
      };
      if (item.properties && Object.keys(item.properties).length > 0) {
        line.attributes = Object.keys(item.properties).map(function (key) {
          return { key: key, value: String(item.properties[key]) };
        });
      }
      return line;
    });
  }
  function ptApplyCartToForm(api, cartInfo) {
    var edges = cartInfo && cartInfo.lines && cartInfo.lines.edges;
    if (!cartInfo || !Array.isArray(edges) || !edges.length) throw new Error("Releasit received an empty cart");
    var first = edges[0] && edges[0].node;
    var merch = first && first.merchandise;
    return api.setState({
      cartInfo: cartInfo,
      products: edges.map(function (edge) { return edge.node.merchandise; }),
      currentVariantId: merch && merch.id ? Number(String(merch.id).split("/").pop()) : undefined,
      currentProductId: merch && merch.product && merch.product.id ? Number(String(merch.product.id).split("/").pop()) : undefined,
      triggerCartUpdate: 0,
      isOpen: true,
      isCartButtonLoading: false,
      isClearingCart: false
    });
  }
  function ptOpenReleasitForm(cart) {
    return ptWaitFor(releasitApi, 10000, 200).then(function (api) {
      if (ptAddMode() === "product-only") {
        var only = ptOpenProductOnly(api, cart);
        if (only) {
          return only.catch(function (err) {
            if (window.console && console.warn) console.warn("[pt] product-only handoff failed; showing the whole cart instead:", err);
            return ptOpenReleasitFormCartMode(api, cart);
          });
        }
      }
      return ptOpenReleasitFormCartMode(api, cart);
    });
  }
  function ptOpenReleasitFormCartMode(api, cart) {
    return Promise.resolve().then(function () {
      var cartId = ptCartIdFromToken(cart);
      if (cartId) {
        /* The /cart path: read the shopper's real cart and show it. Touches nothing. */
        return api.cartService.getCart(cartId).then(function (result) {
          var cartInfo = result && result.data && result.data.cart;
          if (!cartInfo) throw new Error("Releasit could not read the cart");
          return ptApplyCartToForm(api, cartInfo);
        });
      }
      /* Unsignable token: fall back to rebuilding Releasit's own scratch cart. */
      var state = api.getState() || {};
      var ownId = state.cartInfo && state.cartInfo.id;
      if (!ownId) throw new Error("Releasit cart ID is unavailable");
      var lines = ptStorefrontLines(cart);
      if (!lines.length) throw new Error("the Shopify cart is empty");
      return Promise.resolve(api.cartService.clearCart(ownId))
        .then(function () { return api.cartService.addProduct(ownId, lines); })
        .then(function (result) {
          var cartInfo = (result && result.cartLinesAdd && result.cartLinesAdd.cart) || (result && result.cart);
          return ptApplyCartToForm(api, cartInfo);
        });
    });
  }
  function openReleasitFormOnPage(cart) {
    if (releasitFormIsOpen()) return Promise.resolve(true);
    if (ptFormOpenInFlight) return ptFormOpenInFlight;
    ptFormOpenInFlight = ptOpenReleasitForm(cart)
      .then(function () { ptFormOpenInFlight = null; return true; })
      .catch(function (err) {
        ptFormOpenInFlight = null;
        if (window.console && console.warn) console.warn("[pt] Releasit cart-mode handoff failed; trying the app opener:", err);
        if (releasitOpenViaApi()) return true;
        throw err;
      });
    return ptFormOpenInFlight;
  }

  /* Ground-truth confirmation of an add, whoever performed it: our own AJAX call, Vitals'
     interception, or Releasit. Polls /cart.js rather than trusting one
     path's events, so the bridge cannot hang when Vitals swallows the click. */
  function waitForCartChange(opts) {
    var variantId = opts.variantId;
    var beforeQty = opts.beforeQty || 0;
    var beforeCount = opts.beforeCount || 0;
    var timeout = opts.timeout || 12000;
    var step = 300;
    var waited = 0;
    function poll() {
      return fetch("/cart.js", { headers: { Accept: "application/json" }, cache: "no-store" })
        .then(function (r) { if (!r.ok) throw new Error("cart.js HTTP " + r.status); return r.json(); })
        .then(function (cart) {
          var grew = Number(cart.item_count || 0) > beforeCount || cartVariantQty(cart, variantId) > beforeQty;
          if (grew) { paintCartCount(cart.item_count); document.dispatchEvent(new CustomEvent("pt:cart:updated", { detail: cart })); return cart; }
          waited += step;
          if (waited >= timeout) throw new Error("cart never confirmed the add");
          return ptDelay(step).then(poll);
        });
    }
    return poll();
  }

  /* Runs after Vitals has finished ITS add (the tier quantity, plus any claims).
     On the CART page the hooks above have just cancelled the reload Vitals would otherwise
     have done, so the new lines and the Vitals-discounted totals are re-rendered in place —
     without this the cart page would silently stop showing what was just added. */
  function handlePostAdd() {
    var intent = takeBuyIntent();
    return refreshCart()
      .then(function (cart) {
        if (intent !== "form") {
          /* Plain add: re-render the cart page in place so the new line and the
             Vitals-discounted totals show without a reload. */
          if (document.querySelector("[data-cart-lines]")) return rerenderCart();
          return null;
        }
        return openReleasitFormOnPage(cart);
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn("[pt] post-add handling failed:", err);
      });
  }

  var ptVitalsHooked = false;
  function ptInstallVitalsHooks(scope) {
    if (ptVitalsHooked || !scope || typeof scope.vitalsSet !== "function") return;
    ptVitalsHooked = true;
    try {
      scope.vitalsSet("VITALS_HOOK__ON_ATC_STAY_ON_THE_SAME_PAGE", function () { handlePostAdd(); });
      scope.vitalsSet("VITALS_HOOK__OPEN_CART_DRAWER", function () { handlePostAdd(); return true; });
    } catch (e) {
      ptVitalsHooked = false; // let a later invocation try again
      if (window.console && console.warn) console.warn("[pt] could not install Vitals hooks:", e);
    }
  }
  /* Wrap the app-embed's own scope factories. They are plain functions on window and are
     called by Vitals with the hook scope, so wrapping is the supported-shaped way in without
     an app-side Custom JS edit. The wrapper is transparent: same arguments, same return. */
  function ptWrapVitalsScopeFactories() {
    var ld = window.vtlsLiquidData;
    if (!ld) return false;
    var found = false;
    ["dynamicFunctions", "documentInjectors"].forEach(function (key) {
      var orig = ld[key];
      if (typeof orig !== "function") return;
      found = true;
      if (orig.__ptWrapped) return;
      var wrapped = function (scope) {
        try { ptInstallVitalsHooks(scope); } catch (e) {}
        return orig.apply(this, arguments);
      };
      wrapped.__ptWrapped = true;
      ld[key] = wrapped;
    });
    return found;
  }
  function armVitalsHooks() {
    /* Primary path: layout/theme.liquid claims the hook scope from an inline script placed
       ABOVE the Vitals app-embed, and hands it over here — either one already captured
       before this deferred file ran, or via the callback when the embed assigns later.
       The wrap+poll below stays as a backstop for the case where that inline claim could not
       install its accessors (e.g. another script replaced window.vtlsLiquidData wholesale). */
    window.__ptOnVitalsScope = ptInstallVitalsHooks;
    if (window.__ptVitalsScope) ptInstallVitalsHooks(window.__ptVitalsScope);
    if (ptVitalsHooked) return;
    if (ptWrapVitalsScopeFactories() && ptVitalsHooked) return;
    var tries = 0;
    var iv = setInterval(function () {
      ptWrapVitalsScopeFactories();
      if (ptVitalsHooked || ++tries > 120) clearInterval(iv);
    }, 50);
  }

  /* Re-render the CART PAGE line items + totals in place (no full reload) by fetching the
     cart template HTML and swapping the relevant nodes. Used after a cart-page quick-add so
     the new line + the (Liquid-rendered) Vitals-discounted totals appear without navigation.
     The cart is an inline Liquid template (not a section), so we render the template page and
     swap [data-cart-lines] + totals; cart-line qty handlers are delegated on [data-cart] so
     they keep working on the swapped-in nodes. A cart:update event is dispatched so Vitals /
     other listeners can re-process the refreshed cart. */
  function rerenderCart() {
    return fetch("/cart", { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var freshLines = doc.querySelector("[data-cart-lines]");
        var curLines = document.querySelector("[data-cart-lines]");
        if (freshLines && curLines) {
          curLines.innerHTML = freshLines.innerHTML;
        } else {
          // Structure changed (e.g. switched to empty-state) — swap the whole cart container.
          var freshCart = doc.querySelector("[data-cart]"), curCart = document.querySelector("[data-cart]");
          if (freshCart && curCart) curCart.innerHTML = freshCart.innerHTML;
        }
        ["[data-cart-subtotal]", "[data-cart-total]"].forEach(function (sel) {
          var n = doc.querySelector(sel), c = document.querySelector(sel);
          if (n && c) c.textContent = n.textContent;
        });
        document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true, detail: { source: "pt-rerender" } }));
        return refreshCart();
      })
      .catch(function () { return refreshCart(); });
  }

  /* ---------- announcement bar ---------- */
  function initAnnounce(root) {
    var msgs = root.querySelectorAll(".pt-announce__msg");
    if (msgs.length < 2) return null;
    var i = 0, timer = null;
    var delay = parseInt(root.getAttribute("data-rotate-ms"), 10) || 3500;
    function tick() {
      msgs[i].classList.remove("is-active");
      i = (i + 1) % msgs.length;
      msgs[i].classList.add("is-active");
    }
    timer = setInterval(tick, delay);
    return function teardown() { clearInterval(timer); };
  }

  /* ---------- header (hamburger + cart badge) ---------- */
  function initHeader(root) {
    var ac = makeAC();
    var signal = ac.signal;
    var burger = root.querySelector("[data-hamburger]");
    var nav = root.querySelector("[data-mobile-nav]");
    if (burger && nav) {
      burger.addEventListener("click", function () {
        var open = nav.toggleAttribute("data-open");
        burger.setAttribute("aria-expanded", open ? "true" : "false");
        /* The homepage header is transparent by design so the hero shows
           through it. That is right for a 64px bar and wrong for a 377px panel
           hanging off it: the menu was rendering with the video playing behind
           the links. Flag the open state so the bar can go solid, the same way
           it already does once the hero scrolls past (.is-stuck).
           A class, not :has(), because a fifth of sessions here are in-app
           webviews old enough to miss it. */
        var hdr = root.classList.contains("pt-header") ? root : root.querySelector(".pt-header");
        if (hdr) hdr.classList.toggle("is-nav-open", open);
      }, { signal: signal });
    }
    /* Overlay header (homepage): transparent over the hero, solid once the hero
       has scrolled past. body.pt-header-overlay is set in layout/theme.liquid,
       so the transparent state paints on first frame and never flashes. */
    if (document.body.classList.contains("pt-header-overlay")) {
      var hero = document.querySelector(".fn-hero");
      var onScroll = function () {
        var trigger = hero ? hero.offsetHeight - root.offsetHeight - 8 : 8;
        root.classList.toggle("is-stuck", (window.scrollY || window.pageYOffset || 0) > trigger);
      };
      window.addEventListener("scroll", onScroll, { passive: true, signal: signal });
      window.addEventListener("resize", onScroll, { passive: true, signal: signal });
      onScroll();
    }

    refreshCart();
    return function teardown() { ac.abort(); };
  }

  /* ---------- AJAX add to cart (delegated) ---------- */
  function initAtc(root) {
    var ac = makeAC();
    root.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-atc]");
      if (!btn) return;
      e.preventDefault();          // don't follow the card link
      e.stopPropagation();
      var id = btn.getAttribute("data-atc");
      if (!id || btn.getAttribute("aria-busy") === "true") return;
      btn.setAttribute("aria-busy", "true");
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error("cart/add HTTP " + r.status); return r.json(); })
        .then(function (data) {
          if (data && data.status && data.status >= 400) throw new Error("cart/add rejected");
          return refreshCart().catch(function () {
            // optimistic badge bump so the user still sees feedback if refresh fails
            document.querySelectorAll("[data-cart-count]").forEach(function (el) {
              var n = (parseInt(el.textContent, 10) || 0) + 1;
              el.textContent = n > 99 ? "99+" : String(n); el.hidden = false;
            });
          });
        })
        .catch(function (err) { if (window.console && console.error) console.error("[pt-atc] add failed:", err); })
        .then(function () { btn.removeAttribute("aria-busy"); });
    }, { signal: ac.signal, passive: false });
    return function teardown() { ac.abort(); };
  }

  /* ---------- auto-advancing product carousel ([data-auto-carousel]) ----------
     Scrolls the native scroll-snap track one slide at a time and wraps at the
     end. The track works without this (swipe / arrow keys / the nav buttons);
     the script only adds the automatic advance.

     It stops itself whenever the shopper is engaged — pointer over the track,
     keyboard focus inside it, a manual scroll, or the tab being hidden — and
     never starts at all under prefers-reduced-motion or when everything already
     fits on screen and there is nothing to advance to. */
  function initAutoCarousel(root) {
    var track = root.querySelector("[data-carousel-track]");
    if (!track) return null;

    var ac = makeAC(), signal = ac.signal;
    var interval = parseInt(root.getAttribute("data-interval"), 10) || 3500;
    var timer = null, paused = false;

    function slideStep() {
      var slide = track.querySelector(".fn-carousel__slide");
      if (!slide) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }
    function atEnd() {
      return track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    }
    function advance(dir) {
      var step = slideStep() * (dir || 1);
      if (dir !== -1 && atEnd()) track.scrollTo({ left: 0, behavior: "smooth" });
      else track.scrollBy({ left: step, behavior: "smooth" });
    }
    function start() {
      if (timer || paused) return;
      if (track.scrollWidth <= track.clientWidth + 4) return; // nothing to scroll
      timer = setInterval(function () { if (!paused) advance(1); }, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function pause() { paused = true; stop(); }
    function resume() { paused = false; start(); }

    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // still wire the buttons, just never advance on its own
      paused = true;
    }

    root.addEventListener("pointerenter", pause, { signal: signal });
    root.addEventListener("pointerleave", resume, { signal: signal });
    root.addEventListener("focusin", pause, { signal: signal });
    root.addEventListener("focusout", resume, { signal: signal });
    track.addEventListener("touchstart", pause, { signal: signal, passive: true });
    document.addEventListener("visibilitychange", function () {
      if (document.hidden) stop(); else start();
    }, { signal: signal });

    var prev = root.querySelector("[data-carousel-prev]");
    var next = root.querySelector("[data-carousel-next]");
    if (prev) prev.addEventListener("click", function () { advance(-1); }, { signal: signal });
    if (next) next.addEventListener("click", function () { advance(1); }, { signal: signal });

    start();
    return function teardown() { stop(); ac.abort(); };
  }

  /* ---------- PDP gallery placement ----------
     The gallery is rendered at the END of the description (product.liquid) and
     moved here to roughly the middle of it, so the images break up the long
     landing-page copy instead of trailing after all of it.

     The insertion point is the midpoint of the description wrapper's top-level
     panels. Those wrappers differ per product (vxg-root, vxgel-root, …), so the
     midpoint is computed from whatever children exist rather than from a
     hardcoded selector. If the wrapper or the gallery is missing nothing moves,
     and the gallery simply stays where Liquid rendered it. */
  function initPdpGallery(root) {
    var gallery = root.querySelector("[data-pdp-gallery]");
    if (!gallery) return null;
    var descRoot = root.querySelector('.pt-pdp__desc [class$="-root"]') ||
                   root.querySelector(".pt-pdp__desc");
    if (!descRoot) return null;

    var panels = Array.prototype.filter.call(descRoot.children, function (el) {
      return el.nodeType === 1 && el.tagName !== "STYLE" && el.tagName !== "SCRIPT";
    });
    if (panels.length < 2) return null;

    descRoot.insertBefore(gallery, panels[Math.floor(panels.length / 2)]);
    gallery.classList.add("is-inline");
    return null;
  }

  /* ---------- PDP gallery lightbox ----------
     Click a gallery image to open it full-screen and step through the set with
     the arrows, the keyboard (←/→/Esc) or a swipe. The overlay is built on the
     first open, not on page load, so a shopper who never opens it pays nothing.
     Scroll is locked while open and focus returns to the tile that was clicked. */
  function initPdpLightbox(root) {
    var gallery = root.querySelector("[data-pdp-gallery]");
    if (!gallery) return null;

    var ac = makeAC(), signal = ac.signal;
    var box = null, boxImg = null, boxCount = null;
    var slides = [], index = 0, opener = null;

    function collect() {
      slides = Array.prototype.map.call(
        gallery.querySelectorAll(".pt-pdp-gallery__item img"),
        function (img) { return { src: img.currentSrc || img.src, alt: img.alt || "" }; }
      );
    }

    function build() {
      box = document.createElement("div");
      box.className = "pt-lightbox";
      box.setAttribute("role", "dialog");
      box.setAttribute("aria-modal", "true");
      box.setAttribute("aria-label", "Zdjęcia produktu");
      box.innerHTML =
        '<button type="button" class="pt-lightbox__close" aria-label="Zamknij">&times;</button>' +
        '<button type="button" class="pt-lightbox__nav pt-lightbox__nav--prev" aria-label="Poprzednie zdjęcie">&#8249;</button>' +
        '<figure class="pt-lightbox__stage"><img class="pt-lightbox__img" alt=""></figure>' +
        '<button type="button" class="pt-lightbox__nav pt-lightbox__nav--next" aria-label="Następne zdjęcie">&#8250;</button>' +
        '<span class="pt-lightbox__count" aria-live="polite"></span>';
      document.body.appendChild(box);
      boxImg = box.querySelector(".pt-lightbox__img");
      boxCount = box.querySelector(".pt-lightbox__count");

      box.querySelector(".pt-lightbox__close").addEventListener("click", close, { signal: signal });
      box.querySelector(".pt-lightbox__nav--prev").addEventListener("click", function () { step(-1); }, { signal: signal });
      box.querySelector(".pt-lightbox__nav--next").addEventListener("click", function () { step(1); }, { signal: signal });
      // backdrop click only — not a click on the photo itself
      box.addEventListener("click", function (e) { if (e.target === box) close(); }, { signal: signal });

      var startX = null;
      box.addEventListener("touchstart", function (e) { startX = e.touches[0].clientX; }, { signal: signal, passive: true });
      box.addEventListener("touchend", function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 45) step(dx < 0 ? 1 : -1);
        startX = null;
      }, { signal: signal, passive: true });
    }

    function show(i) {
      index = (i + slides.length) % slides.length;
      boxImg.src = slides[index].src;
      boxImg.alt = slides[index].alt;
      boxCount.textContent = (index + 1) + " / " + slides.length;
      var multi = slides.length > 1;
      box.querySelector(".pt-lightbox__nav--prev").hidden = !multi;
      box.querySelector(".pt-lightbox__nav--next").hidden = !multi;
      boxCount.hidden = !multi;
    }

    function step(d) { show(index + d); }

    function open(i, from) {
      collect();
      if (!slides.length) return;
      if (!box) build();
      opener = from || null;
      show(i);
      box.classList.add("is-open");
      document.documentElement.classList.add("pt-lightbox-open");
      box.querySelector(".pt-lightbox__close").focus();
    }

    function close() {
      if (!box) return;
      box.classList.remove("is-open");
      document.documentElement.classList.remove("pt-lightbox-open");
      if (opener && typeof opener.focus === "function") opener.focus();
      opener = null;
    }

    gallery.addEventListener("click", function (e) {
      var fig = e.target.closest(".pt-pdp-gallery__item");
      if (!fig) return;
      e.preventDefault();
      var figs = Array.prototype.slice.call(gallery.querySelectorAll(".pt-pdp-gallery__item"));
      open(figs.indexOf(fig), fig);
    }, { signal: signal });

    // Tiles are buttons in spirit: make them reachable and operable by keyboard.
    Array.prototype.forEach.call(gallery.querySelectorAll(".pt-pdp-gallery__item"), function (fig, i) {
      fig.tabIndex = 0;
      fig.setAttribute("role", "button");
      fig.setAttribute("aria-label", "Powiększ zdjęcie " + (i + 1));
      fig.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(i, fig); }
      }, { signal: signal });
    });

    document.addEventListener("keydown", function (e) {
      if (!box || !box.classList.contains("is-open")) return;
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "ArrowRight") step(1);
    }, { signal: signal });

    return function teardown() {
      ac.abort();
      if (box) box.remove();
      document.documentElement.classList.remove("pt-lightbox-open");
    };
  }

  /* ---------- card quantity picker ([data-cart-stepper]) ----------
     CONTRACT CHANGED 2026-09-08. The number used to BE the cart line quantity,
     written straight through with absolute /cart/update.js writes. It is now a
     LOCAL picker that starts at 1, and the cart button adds that many at once —
     the same contract the Hungarian store uses.

     What that buys: the shopper states an intent before anything is written, so
     a mistyped 5 costs nothing, and one press carries the whole quantity into
     the Vitals volume offer as a single line.

     What it costs: /cart/add.js is RELATIVE, so a second press adds again on
     top. That is deliberate (press add twice, get twice), but a double-fire
     from one tap is not — hence the busy latch held for the whole round trip.
     Removal goes through /cart/update.js at quantity 0, which is absolute and
     needs no line key, so repeat taps cannot drive it negative. */

  function pickerRead(wrap) {
    var inp = wrap.querySelector(".fn-pg__qty-input");
    var max = parseInt(inp && inp.max, 10) || 10;
    return Math.min(max, Math.max(1, parseInt(inp && inp.value, 10) || 1));
  }

  function pickerWrite(wrap, n) {
    var inp = wrap.querySelector(".fn-pg__qty-input");
    if (inp) inp.value = n;
    var minus = wrap.querySelector("[data-rqty-minus]");
    if (minus) minus.disabled = n <= 1;
  }

  /* The button's three faces are pure CSS off .is-in-cart; only the label needs
     JS, because a screen reader would otherwise still offer "add" on a button
     that now removes. */
  function paintInCart(wrap, inCart) {
    var card = wrap.closest(".fn-pg__card");
    if (!card) return;
    card.classList.toggle("is-in-cart", !!inCart);
    if (!inCart) card.classList.remove("is-just-added");
    var toggle = card.querySelector("[data-cart-toggle]");
    if (toggle) {
      var t = toggle.getAttribute("data-title") || "";
      toggle.setAttribute("aria-label",
        (inCart ? "Usu\u0144 " : "Dodaj ") + t + (inCart ? " z koszyka" : " do koszyka"));
    }
  }

  function headerCartEl() {
    var sels = ["[data-cart-icon]", "cart-icon", ".pt-header__cart", ".header__cart", 'a[href$="/cart"]', 'a[href*="/cart"]'];
    for (var i = 0; i < sels.length; i++) {
      var el = document.querySelector(sels[i]);
      if (el && el.getBoundingClientRect().width) return el;
    }
    return null;
  }

  /* Product image arcs up to the header cart icon, which then bumps. Purely
     decorative: bails out on reduced-motion or if WAAPI/the icon is missing,
     and never blocks the cart write. */
  function flyToCart(card) {
    try {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      var img = card.querySelector(".fn-pg__img");
      var target = headerCartEl();
      if (!img || !target || typeof img.animate !== "function") return;
      var from = img.getBoundingClientRect();
      var to = target.getBoundingClientRect();
      if (!from.width || !to.width) return;

      var size = Math.min(from.width, 120);
      var ghost = document.createElement("img");
      ghost.src = img.currentSrc || img.src;
      ghost.alt = "";
      ghost.className = "fn-fly-ghost";
      ghost.setAttribute("aria-hidden", "true");
      ghost.style.left = (from.left + (from.width - size) / 2) + "px";
      ghost.style.top = (from.top + (from.height - size) / 2) + "px";
      ghost.style.width = size + "px";
      ghost.style.height = size + "px";
      document.body.appendChild(ghost);

      var dx = to.left + to.width / 2 - (from.left + from.width / 2);
      var dy = to.top + to.height / 2 - (from.top + from.height / 2);
      var anim = ghost.animate([
        { transform: "translate(0,0) scale(1)", opacity: 1 },
        { transform: "translate(" + (dx * 0.55) + "px," + (dy * 0.5 - 60) + "px) scale(.55)", opacity: .9, offset: .6 },
        { transform: "translate(" + dx + "px," + dy + "px) scale(.12)", opacity: .15 }
      ], { duration: 700, easing: "cubic-bezier(.5,-0.2,.7,1)" });
      anim.onfinish = function () {
        ghost.remove();
        target.classList.add("fn-cart-bump");
        setTimeout(function () { target.classList.remove("fn-cart-bump"); }, 520);
      };
      anim.oncancel = function () { ghost.remove(); };
    } catch (e) { /* animation is cosmetic — never let it break the add */ }
  }

  function addToCart(wrap, btn) {
    var id = wrap.dataset.variantId;
    if (!id) return;
    var qty = pickerRead(wrap);
    var card = wrap.closest(".fn-pg__card");

    btn.disabled = true;
    btn.setAttribute("data-busy", "");
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: id, quantity: qty })
    })
      .then(function (r) { if (!r.ok) throw new Error("add " + r.status); return r.json(); })
      .then(function () {
        paintInCart(wrap, true);
        if (card) {
          card.classList.remove("is-just-added");
          void card.offsetWidth; // reflow so the pop restarts on a repeat add
          card.classList.add("is-just-added");
          flyToCart(card);
        }
        /* 2026-09-09: on the cart page the lines are inline Liquid, so refreshCart() only
           repaints the badge — a quick-add from the "Klienci kupują również" carousel left
           the list stale until a manual reload (owner-reported). Re-render the lines in
           place. rerenderCart() swaps [data-cart-lines] + the totals only; the Releasit
           #checkout button lives in .pt-cart__summary, outside that node, so it is never
           touched, and the cart-line qty handlers are delegated on [data-cart] so they
           survive the swap. rerenderCart() ends with refreshCart(), so the badge still
           updates. Off the cart page [data-cart-lines] does not exist and behaviour is
           unchanged. */
        if (document.querySelector("[data-cart-lines]")) return rerenderCart();
        return refreshCart();
      })
      .catch(function (err) {
        if (window.console && console.error) console.error("[pt-card] add failed:", err);
      })
      .then(function () { btn.disabled = false; btn.removeAttribute("data-busy"); });
  }

  function removeFromCart(wrap, btn) {
    var id = wrap.dataset.variantId;
    if (!id) return;
    var updates = {};
    updates[id] = 0;

    btn.disabled = true;
    btn.setAttribute("data-busy", "");
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { if (!r.ok) throw new Error("update " + r.status); return r.json(); })
      .then(function () {
        paintInCart(wrap, false);
        pickerWrite(wrap, 1); // back to the resting state, ready to pick again
        /* Same reason as the add path above — keep the cart page list in sync. */
        if (document.querySelector("[data-cart-lines]")) return rerenderCart();
        return refreshCart();
      })
      .catch(function (err) {
        if (window.console && console.error) console.error("[pt-card] remove failed:", err);
      })
      .then(function () { btn.disabled = false; btn.removeAttribute("data-busy"); });
  }

  /* ---------- fn- card quick-add (qty stepper + data-quick-add AJAX) ----------
     Powers the homepage fn-product-grid (.fn-pg__card) and fn-product-spotlight
     ATC buttons, which use [data-quick-add]/[data-variant-id] + a [data-rqty-*]
     stepper (a different contract from the pt- [data-atc] cards). Delegated at
     the scope root; listeners are torn down via the AbortController signal. */
  function initQuickAdd(root) {
    var ac = makeAC(), signal = ac.signal;

    // qty stepper (+/-)
    root.addEventListener("click", function (e) {
      var toggleBtn = e.target.closest("[data-cart-toggle]");
      if (toggleBtn && !toggleBtn.disabled) {
        e.preventDefault();
        e.stopPropagation();
        var tCard = toggleBtn.closest(".fn-pg__card");
        var tWrap = tCard && tCard.querySelector("[data-cart-stepper]");
        if (!tWrap) return;
        var inCart = tCard.classList.contains("is-in-cart");
        // multi-variant products still need a choice made on the PDP
        if (!inCart && toggleBtn.dataset.multipleVariants === "true") {
          if (toggleBtn.dataset.productUrl) window.location.href = toggleBtn.dataset.productUrl;
          return;
        }
        if (inCart) removeFromCart(tWrap, toggleBtn);
        else addToCart(tWrap, toggleBtn);
        return;
      }

      var btn = e.target.closest("[data-rqty-minus],[data-rqty-plus]");
      if (!btn || btn.disabled) return;

      // fn-pg cards: the stepper only picks a number. Nothing reaches the cart
      // until the button beside it is pressed, so a mis-tap costs nothing.
      var cartWrap = btn.closest("[data-cart-stepper]");
      if (cartWrap) {
        e.preventDefault();
        var max = parseInt(cartWrap.querySelector(".fn-pg__qty-input").max, 10) || 10;
        var cur = pickerRead(cartWrap);
        pickerWrite(cartWrap, btn.hasAttribute("data-rqty-plus")
          ? Math.min(max, cur + 1)
          : Math.max(1, cur - 1));
        return;
      }

      // everything else keeps the local-only picker (spotlight, cart reco, …)
      var wrap = btn.closest(".fn-pg__qty-wrap, .fn-cart-reco__qty-wrap, .vx-related__qty-wrap");
      var inp = wrap && wrap.querySelector(".fn-pg__qty-input, .fn-cart-reco__qty-input, .vx-related__qty-input");
      if (!inp) return;
      var val = parseInt(inp.value, 10) || 1;
      var min = parseInt(inp.min, 10) || 1;
      var max = parseInt(inp.max, 10) || 99;
      if (btn.hasAttribute("data-rqty-plus")) { if (val < max) inp.value = val + 1; }
      else { if (val > min) inp.value = val - 1; }
    }, { signal: signal, passive: false });

    // quick-add button
    root.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-quick-add]");
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      var variantId = btn.dataset.variantId;
      if (!variantId) return;
      // multi-variant products: send the shopper to the PDP to choose
      if (btn.dataset.multipleVariants === "true") {
        if (btn.dataset.productUrl) window.location.href = btn.dataset.productUrl;
        return;
      }
      var card = btn.closest(".fn-pg__card, .fn-cart-reco__card");
      var qtyInp = card && card.querySelector(".fn-pg__qty-input, .fn-cart-reco__qty-input");
      var qty = qtyInp ? (parseInt(qtyInp.value, 10) || 1) : 1;
      // icon buttons hold an <svg>; never overwrite textContent — convey state via classes
      var hasMarkup = !!btn.querySelector("svg, img, span");
      var origText = btn.textContent;
      if (btn.getAttribute("aria-busy") === "true") return;
      btn.setAttribute("aria-busy", "true");
      btn.disabled = true;
      btn.classList.add("is-loading");
      if (!hasMarkup) btn.textContent = "…";
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty })
      })
        .then(function (r) { if (!r.ok) throw new Error("add"); return r.json(); })
        .then(function () { return refreshCart(); })
        .then(function () {
          btn.classList.remove("is-loading");
          btn.classList.add("is-added");
          if (!hasMarkup) btn.textContent = "✓ Dodano!";
          // On the CART PAGE the line items / totals / Vitals bundle discount are
          // rendered by the Liquid template (not a section). Instead of a full reload,
          // re-render the cart lines + totals in place (rerenderCart) so the new line +
          // discounted totals appear with no navigation. Only reaches here inside the
          // resolved/success branch, so a failed add never re-renders.
          var onCartPage = !!document.querySelector(".pt-cart[data-cart], [data-cart-lines]");
          if (onCartPage) {
            return rerenderCart().then(function () {
              setTimeout(function () {
                btn.disabled = false;
                btn.removeAttribute("aria-busy");
                btn.classList.remove("is-added");
                if (!hasMarkup) btn.textContent = origText;
              }, 1200);
            });
          }
          setTimeout(function () {
            btn.disabled = false;
            btn.removeAttribute("aria-busy");
            btn.classList.remove("is-added");
            if (!hasMarkup) btn.textContent = origText;
          }, 2000);
        })
        .catch(function (err) {
          if (window.console && console.error) console.error("[pt-quickadd] add failed:", err);
          btn.classList.remove("is-loading", "is-added");
          btn.disabled = false;
          btn.removeAttribute("aria-busy");
          if (!hasMarkup) btn.textContent = origText;
        });
    }, { signal: signal, passive: false });

    return function teardown() { ac.abort(); };
  }

  function formatMoney(cents, currency) {
    var lang = (document.documentElement.lang || "pl").slice(0, 2);
    try {
      var s = new Intl.NumberFormat(lang, { style: "currency", currency: currency || "PLN" }).format(cents / 100);
      return s.replace(/[.,]00(\s*\D+)$/, "$1"); // "129,00 zł" -> "129 zł"
    } catch (e) { return (cents / 100).toFixed(2); }
  }

  /* ---------- testimonials carousel ---------- */
  function initTestimonials(root) {
    var track = root.querySelector("[data-tst-track]");
    if (!track) return null;
    var cards = Array.prototype.slice.call(track.children);
    var prev = root.querySelector("[data-tst-prev]");
    var next = root.querySelector("[data-tst-next]");
    var dotsWrap = root.querySelector("[data-tst-dots]");
    if (cards.length < 2) { if (prev) prev.style.display = "none"; if (next) next.style.display = "none"; return null; }
    var ac = makeAC(), signal = ac.signal;
    var timer = null;

    var dots = cards.map(function (_, i) {
      var b = document.createElement("button");
      b.type = "button"; b.setAttribute("aria-label", "Opinia " + (i + 1));
      b.addEventListener("click", function () { cards[i].scrollIntoView({ block: "nearest", inline: "start" }); }, { signal: signal });
      if (dotsWrap) dotsWrap.appendChild(b);
      return b;
    });

    function activeIndex() {
      var c = track.getBoundingClientRect();
      var best = 0, bestD = Infinity;
      cards.forEach(function (card, i) {
        var d = Math.abs(card.getBoundingClientRect().left - c.left);
        if (d < bestD) { bestD = d; best = i; }
      });
      return best;
    }
    function paint() {
      var a = activeIndex();
      dots.forEach(function (d, i) { d.classList.toggle("is-active", i === a); });
    }
    function step(dir) {
      var i = Math.min(cards.length - 1, Math.max(0, activeIndex() + dir));
      cards[i].scrollIntoView({ block: "nearest", inline: "start" });
    }
    if (prev) prev.addEventListener("click", function () { step(-1); stop(); }, { signal: signal });
    if (next) next.addEventListener("click", function () { step(1); stop(); }, { signal: signal });
    track.addEventListener("scroll", function () { paint(); }, { signal: signal, passive: true });

    function start() { stop(); timer = setInterval(function () {
      var i = activeIndex();
      var nx = i + 1 >= cards.length ? 0 : i + 1;
      cards[nx].scrollIntoView({ block: "nearest", inline: "start" });
    }, 4500); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    root.addEventListener("pointerenter", stop, { signal: signal });
    root.addEventListener("pointerleave", start, { signal: signal });
    root.addEventListener("touchstart", stop, { signal: signal, passive: true });

    paint(); start();
    return function teardown() { ac.abort(); stop(); if (dotsWrap) dotsWrap.textContent = ""; };
  }

  /* ---------- PDP gallery ---------- */
  function initGallery(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll("[data-gallery-slide]"));
    if (!slides.length) return null;
    var dots = Array.prototype.slice.call(root.querySelectorAll("[data-gallery-dot]"));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-gallery-thumb]"));
    var stage = root.querySelector("[data-gallery-stage]");
    var ac = makeAC(), signal = ac.signal;
    var current = 0;

    function setActive(i) {
      i = (i + slides.length) % slides.length;
      slides.forEach(function (s, k) { s.classList.toggle("is-active", k === i); if (k !== i) s.classList.remove("is-zoomed"); });
      dots.forEach(function (d, k) { d.classList.toggle("is-active", k === i); });
      thumbs.forEach(function (t, k) { t.classList.toggle("is-active", k === i); });
      current = i;
    }
    dots.forEach(function (d, i) { d.addEventListener("click", function () { setActive(i); }, { signal: signal }); });
    thumbs.forEach(function (t, i) { t.addEventListener("click", function () { setActive(i); }, { signal: signal }); });

    function activeImg() { var act = slides[current]; return act ? act.querySelector("[data-gallery-img]") : null; }
    function resetZoom() {
      var im = activeImg();
      if (im) { im.style.transform = ""; im.style.transformOrigin = ""; }
      slides.forEach(function (s) { s.classList.remove("is-zoomed"); });
    }

    // swipe + tap-zoom (pointer) — pinch is excluded by tracking touch count below
    var sx = 0, sy = 0, moved = false, pinching = false;
    if (stage) {
      stage.addEventListener("pointerdown", function (e) { sx = e.clientX; sy = e.clientY; moved = false; }, { signal: signal });
      stage.addEventListener("pointerup", function (e) {
        if (pinching) return; // pinch gesture handled separately
        var dx = e.clientX - sx, dy = e.clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { resetZoom(); setActive(current + (dx < 0 ? 1 : -1)); moved = true; }
        if (!moved && Math.abs(dx) < 8 && Math.abs(dy) < 8) {
          var act = slides[current]; if (act) act.classList.toggle("is-zoomed");
        }
      }, { signal: signal });

      // pinch-to-zoom (two-finger touch) — ported from NightForce gallery behavior
      var pinchStart = 0, pinchImg = null;
      stage.addEventListener("touchstart", function (e) {
        if (e.touches.length === 2) {
          pinching = true;
          pinchImg = activeImg();
          pinchStart = Math.hypot(
            e.touches[1].clientX - e.touches[0].clientX,
            e.touches[1].clientY - e.touches[0].clientY
          );
        }
      }, { signal: signal, passive: true });
      stage.addEventListener("touchmove", function (e) {
        if (e.touches.length !== 2 || !pinchImg || !pinchStart) return;
        var dist = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY
        );
        var scale = Math.max(1, Math.min(4, dist / pinchStart));
        pinchImg.style.transform = "scale(" + scale + ")";
        if (slides[current]) slides[current].classList.toggle("is-zoomed", scale > 1.05);
      }, { signal: signal, passive: true });
      stage.addEventListener("touchend", function (e) {
        if (e.touches.length >= 2) return;
        if (pinchImg) {
          var m = (pinchImg.style.transform || "").match(/scale\(([^)]+)\)/);
          if (m && parseFloat(m[1]) < 1.05) resetZoom();
        }
        if (e.touches.length === 0) { pinching = false; pinchStart = 0; pinchImg = null; }
      }, { signal: signal, passive: true });
    }
    return function teardown() { ac.abort(); };
  }

  /* ---------- PDP form (variant / qty / AJAX add / buy now) ---------- */
  function initProduct(root) {
    var ac = makeAC(), signal = ac.signal;
    var variantSel = root.querySelector("[data-pdp-variant]");
    var priceEl = root.querySelector("[data-pdp-price]");
    var qtyInput = root.querySelector("[data-qty-input]");
    var addBtn = root.querySelector("[data-pdp-add]");

    if (variantSel && variantSel.tagName === "SELECT") {
      variantSel.addEventListener("change", function () {
        var opt = variantSel.options[variantSel.selectedIndex];
        if (opt && priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
      }, { signal: signal });
    }
    /* Upper bound on line quantity (2026-08-10).
       The qty input shipped as type="number" min="1" with NO max, and the stepper below
       incremented with no ceiling, so an arbitrary quantity could reach order creation.
       That is not hypothetical: order #2253 (2026-07-27) carried a Kamagra Gold line of
       1000 units — 1,000 of that product's 1,329 units for the whole 30-day window. It was
       voided 48 minutes later, but on a CASH-ON-DELIVERY store there is no card
       authorisation to fail first, so nothing but that manual catch stood between the order
       and the merchant picking, packing and dispatching it.
       24 sits far above every real order seen in the data (the largest genuine one is 10,
       and the Vitals tiers top out at 3) while making a 1000-unit order impossible.
       Mirrored by max="24" on the input itself, so the clamp holds with JS disabled too. */
    var QTY_MAX = 24;
    function clampQty(n) {
      n = parseInt(n, 10);
      if (isNaN(n) || n < 1) n = 1;
      return Math.min(QTY_MAX, n);
    }

    root.addEventListener("click", function (e) {
      /* Null-guarded: initProduct binds to [data-product], which is not guaranteed to
         contain a qty input. Reading .value off null inside a click handler would throw
         on every tap in that subtree. */
      if (!qtyInput) return;
      var dec = e.target.closest("[data-qty-dec]");
      var inc = e.target.closest("[data-qty-inc]");
      if (!dec && !inc) return;
      var before = qtyInput.value;
      qtyInput.value = clampQty((parseInt(qtyInput.value, 10) || 1) + (inc ? 1 : -1));
      if (qtyInput.value === before) return;
      /* Assigning .value fires nothing on its own, so every listener downstream — the Vitals
         tier sync, anything added later — would silently miss a stepper tap.
         One dispatch here keeps the stepper and a typed edit indistinguishable to the rest of
         the page. initVitalsSync already dispatches the same pair when IT drives the input, so
         the two directions stay symmetrical. */
      qtyInput.dispatchEvent(new Event("input", { bubbles: true }));
      qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: signal });

    /* Typed input is clamped on the way out too — the stepper is not the only way in. */
    if (qtyInput) {
      qtyInput.addEventListener("change", function () {
        qtyInput.value = clampQty(qtyInput.value);
      }, { signal: signal });
    }

    function currentId() { return variantSel ? Number(variantSel.value) : null; }
    function currentQty() { return clampQty(qtyInput && qtyInput.value); }

    var form = root.querySelector("[data-pdp-form]");
    // Write state copy to the inner text span, NOT the whole button: the ATC holds
    // an SVG cart icon (<span.pt-pdp__add-ic>) alongside the label. Assigning
    // addBtn.textContent would collapse the button to a single text node and wipe
    // the icon permanently after the first add — the exact trap the quick-add path
    // guards against with its hasMarkup check. Fall back to the button only if the
    // label span is somehow absent.
    var addLabelEl = (addBtn && addBtn.querySelector(".pt-pdp__add-txt")) || addBtn;
    var addOrigText = addLabelEl ? addLabelEl.textContent : "";

    /* 2026-09-06: when Vitals intercepts the add (the default on every discounted PDP) the
       theme's add() below never runs, so the main button gave no feedback - only the small
       header badge moved, and shoppers tapped again (stacking quantity) or left. Confirm on
       the real cart event, exactly like the sticky bar does. The theme's own add() paints
       itself, so it clears the flag first to avoid a double flash. A flag older than 15s is
       stale (the add never landed) and must not claim an unrelated cart event. */
    var mainAddPending = 0;
    function flashMainAdded() {
      if (!addBtn) return;
      addBtn.classList.remove("is-loading"); addBtn.classList.add("is-added");
      if (addLabelEl) addLabelEl.textContent = "✓ Dodano do koszyka";
      setTimeout(function () {
        addBtn.classList.remove("is-added");
        if (addLabelEl) addLabelEl.textContent = addOrigText;
        addBtn.removeAttribute("aria-busy");
      }, 2000);
    }
    document.addEventListener("pt:cart:updated", function () {
      if (!mainAddPending) return;
      var fresh = (Date.now() - mainAddPending) < 15000;
      mainAddPending = 0;
      if (fresh) flashMainAdded();
    }, { signal: signal });

    // Native form submit fallback: posts to action="/cart/add" and lands on /cart.
    // Used when the AJAX add fails so the item is STILL added rather than silently
    // swallowed. Detach our submit handler first so we don't re-intercept it.
    function nativeSubmit() {
      if (!form) { window.location.href = "/cart"; return; }
      ac.abort(); // remove our preventDefault submit handler
      if (typeof form.requestSubmit === "function") form.requestSubmit();
      else form.submit();
    }

    function add() {
      var id = currentId(); if (!id) return Promise.resolve();
      mainAddPending = 0; // the theme path paints its own success state below
      if (addBtn) { addBtn.setAttribute("aria-busy", "true"); addBtn.classList.add("is-loading"); }
      return fetch("/cart/add.js", {
        method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: id, quantity: currentQty() }] })
      }).then(function (r) {
          if (!r.ok) throw new Error("cart/add HTTP " + r.status);
          return r.json();
        })
        .then(function (data) {
          if (data && data.status && data.status >= 400) throw new Error("cart/add rejected: " + (data.description || data.message || data.status));
          // refreshCart updates the header [data-cart-count] badge. If it fails,
          // optimistically bump the badge so the user still gets visible feedback.
          return refreshCart().catch(function () {
            document.querySelectorAll("[data-cart-count]").forEach(function (el) {
              var n = (parseInt(el.textContent, 10) || 0) + currentQty();
              el.textContent = n > 99 ? "99+" : String(n); el.hidden = false;
            });
          });
        })
        .then(function () {
          // Visible success state on the button itself (not just the small badge).
          if (addBtn) {
            addBtn.classList.remove("is-loading"); addBtn.classList.add("is-added");
            if (addLabelEl) addLabelEl.textContent = "✓ Dodano do koszyka";
            setTimeout(function () {
              addBtn.classList.remove("is-added");
              if (addLabelEl) addLabelEl.textContent = addOrigText;
              addBtn.removeAttribute("aria-busy");
            }, 2000);
          }
        })
        .catch(function (err) {
          // NEVER silently swallow. Surface the error, then fall back to the native
          // form submit so the item is still added for the user.
          if (window.console && console.error) console.error("[pt-pdp] AJAX add failed; falling back to native submit:", err);
          if (addBtn) { addBtn.classList.remove("is-loading"); addBtn.removeAttribute("aria-busy"); }
          nativeSubmit();
        });
    }

    if (form) form.addEventListener("submit", function (e) { e.preventDefault(); add(); }, { signal: signal });

    /* PDP "Kup teraz" → same-page Releasit COD form, with Vitals owning the add.

       Releasit's own PDP handler adds the product through ITS cart, which bypasses Vitals
       entirely — the tier discount is lost. So we intercept that
       click first and instead route the shopper through the ONE path Vitals watches: a real
       click on the theme's add-to-cart button inside form[action="/cart/add"].

       From there either Vitals takes the click over (a volume tier is active — it adds the
       tier quantity itself and, thanks to the hooks installed above, stays on this
       page) or, when Vitals has nothing to apply, the theme's own AJAX add runs. Both end
       with the item in the Shopify cart, so the bridge confirms against /cart.js rather than
       assuming which one ran — the earlier version listened only for the theme's own
       pt:cart:updated event and hung forever on exactly the tier-3 carts this exists for,
       because Vitals had stopped propagation before the theme handler could fire.

       Only then is the COD form opened over the PDP. The shopper is never sent to /cart. */
    var releasitBridgeBusy = false;

    function bridgeToSamePageReleasit(btn) {
      if (releasitBridgeBusy) return;
      releasitBridgeBusy = true;
      var id = currentId();
      var addConfirmed = false;
      if (btn) { btn.setAttribute("aria-busy", "true"); btn.setAttribute("data-pt-rsi-bridge-busy", "1"); }
      /* Scoped to THIS bridge run: the native-submit suppressor below must not outlive it.
         When Vitals takes the click over it cancels the click itself, so no submit event ever
         fires and a {once:true} listener would sit armed indefinitely — silently swallowing
         the next form submit anywhere on the page (newsletter, search, …). */
      var runAc = makeAC();
      function release() {
        releasitBridgeBusy = false;
        runAc.abort();
        if (btn) { btn.removeAttribute("aria-busy"); btn.removeAttribute("data-pt-rsi-bridge-busy"); }
      }
      /* Tells whichever path ends up performing the add that this tap was a BUY, so the
         Vitals hook opens the form instead of just repainting the cart badge. */
      setBuyIntent("form");
      ptBuyVariantId = id; // P7: product-only mode opens the form with this variant alone

      refreshCart({ strict: true })
        .then(function (before) {
          /* 2026-09-06: "Kup teraz" means "buy what I chose NOW", not "add it once more".
             If this variant is already in the cart (an earlier "Dodaj do koszyka", the sticky
             bar, or a cart left over from a previous visit) the add below would stack on top
             of it and the COD form would open with 4 pieces at ~400 zl when the shopper picked
             2 at 195,99. Zero that line first so the add lands exactly the chosen quantity.
             Other products in the cart are left alone - cart-mode is meant to carry them.
             If the reset itself fails we fall through to the old behaviour rather than
             leaving the tap dead. */
          var staleQty = cartVariantQty(before, id);
          if (!(staleQty > 0)) return before;
          var updates = {}; updates[id] = 0;
          return fetch("/cart/update.js", {
            method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ updates: updates })
          })
            .then(function (r) { if (!r.ok) throw new Error("cart/update HTTP " + r.status); return r.json(); })
            .catch(function (err) {
              if (window.console && console.warn) console.warn("[pt-pdp] could not reset the stale cart line; adding on top:", err);
              return before;
            });
        })
        .then(function (before) {
          var beforeQty = cartVariantQty(before, id);
          var beforeCount = before ? Number(before.item_count || 0) : 0;
          if (!addBtn) throw new Error("native add-to-cart button is unavailable");
          /* Cancel only the browser's native form navigation. Vitals' interception lives on
             document capture and does not check defaultPrevented, so it still runs; the
             theme's own submit handler (bubble phase) also still runs when Vitals passes. */
          window.addEventListener("submit", function (event) {
            event.preventDefault();
          }, { once: true, capture: true, signal: runAc.signal });
          addBtn.click();
          return waitForCartChange({ variantId: id, beforeQty: beforeQty, beforeCount: beforeCount, timeout: 12000 });
        })
        .then(function (cart) {
          addConfirmed = true;
          takeBuyIntent(); // this path owns the open from here; don't let the hook double-fire
          return openReleasitFormOnPage(cart);
        })
        .then(function () { release(); })
        .catch(function (err) {
          /* Never fall through to Releasit's own PDP handler: that path adds the product a
             second time and drops Vitals' tier quantity and claims.
             If the add itself succeeded but the form could not be opened, the shopper is
             standing on a page where their tap did nothing while their cart quietly holds the
             order. That is the one case worth the /cart hand-off — the COD form works there,
             so it costs a page load instead of the sale. Anything else keeps them in place. */
          if (window.console && console.error) console.error("[pt-pdp] Releasit same-page cart bridge could not open:", err);
          release();
          if (addConfirmed) window.location.href = "/cart";
        });
    }

    /* Releasit's injected button, plus the theme's own fallback trigger (shown only if
       Releasit never injects one — see initCodFallback), both open the form in place.

       Bound on DOCUMENT, not on [data-product]: Releasit owns where it renders its button and
       does not always place it inside the product section (floating/sticky placements sit at
       body level). A root-scoped listener silently missed those, so Releasit's own handler ran
       instead — which adds the product through ITS cart and lands the shopper on /cart. */
    document.addEventListener("click", function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var btn = target.closest("#rsi_buy_now_button") || target.closest("[data-pt-cod]");
      if (!btn) return;
      /* Never touch a button that belongs to a cart context. The bridge drives the PDP's own
         add-to-cart button, which does not exist there — swallowing such a click would leave
         a dead checkout button. Relevant if a product section is ever rendered on the cart
         page (related products), which would bind this listener there too. */
      if (btn.closest("[data-cart]") || !addBtn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      bridgeToSamePageReleasit(btn);
    }, { capture: true, signal: signal });

    /* A plain add-to-cart tap is an ADD, not a buy: record it so the Vitals hook repaints
       the badge and leaves the shopper on the page instead of opening the COD form.
       2026-09-06: also arm the visible confirmation (flashMainAdded) - when Vitals takes the
       add over, the theme's add() never runs and the button used to show nothing at all. */
    if (addBtn) {
      addBtn.addEventListener("click", function () {
        if (!releasitBridgeBusy) setBuyIntent("cart");
      }, { signal: signal });
      /* Vitals stops propagation at DOCUMENT capture, so the button's own listener above
         never runs on a discounted PDP (verified on the preview theme 2026-09-06). WINDOW
         capture fires before document capture, so this is the one place that sees every
         tap. Ignore taps made by the Releasit bridge (it clicks this button itself). */
      window.addEventListener("click", function (e) {
        var t = e.target;
        if (!t || !t.closest || t.closest("[data-pdp-add]") !== addBtn) return;
        if (!releasitBridgeBusy) mainAddPending = Date.now();
      }, { capture: true, signal: signal });
    }

    /* 2026-09-06: the mobile sticky bar says the same words as the buy-box button
       ("Kup teraz"), so it must open the same COD form. Hand it the bridge. */
    root.__ptBridge = bridgeToSamePageReleasit;

    return function teardown() { ac.abort(); if (root.__ptBridge === bridgeToSamePageReleasit) delete root.__ptBridge; };
  }

  /* ---------- Releasit Buy Now button relocation ----------
     GROUND TRUTH (live audit 2026-06-14): the Releasit COD app embed injects its
     OWN buy-now button as a standalone element `#rsi_buy_now_button`
     (text "Kup teraz"). In embed mode it does NOT populate the
     `._rsi-buy-now-button-app-block-hook` (that hook stays empty — it only holds
     the COD-form anchor); we detect `#rsi_buy_now_button` directly.

     Behavior: relocate the Releasit-owned button into [data-rsi-buynow-slot]
     (directly below Add-to-cart) so the PDP buy box is exactly ATC + Releasit
     "Kup teraz". There is no theme buy-now fallback button — Releasit fully owns
     the buy-now control.
     Memory hygiene: MutationObserver + a bounded poll, all torn down on unload. */
  function initReleasitSlot() {
    /* DO NOT relocate Releasit's buy-now button.
       GROUND TRUTH (browser-verified 2026-06-22): the previous version moved #rsi_buy_now_button
       out of Releasit's own container (#buttonContainerProduct) into [data-rsi-buynow-slot]
       (slot.appendChild) the instant it appeared — i.e. DURING Releasit's skeleton phase. That
       move broke Releasit's skeleton→popup wiring, so clicking it fell back to add+redirect-to-
       /cart instead of opening the COD popup. Left untouched, Releasit renders its button inside
       form.pt-pdp__form right below the Add-to-cart button (~19px gap, full width) and clicking it
       OPENS the COD popup on the PDP with no /cart redirect. The empty [data-rsi-buynow-slot]
       placeholder collapses to 0 height, so the buy box layout is unchanged (ATC → Kup teraz). */
    return null;
  }

  /* ---------- PDP description lazy media (LCP mitigation) ----------
     The description HTML had its img src/srcset rewritten to data-pt-src /
     data-pt-srcset in product.liquid so the heavy GIFs are NOT fetched on
     initial paint. Restore them only when the description scrolls near the
     viewport via IntersectionObserver. Falls back to immediate restore if
     IntersectionObserver is unavailable. */
  /* ---------- COD form fallback trigger (PDP) ----------
     Releasit's own "Kup teraz" button is app-owned and can be absent (app-side placement
     off, bundle blocked, slow network). Without it the PDP has no way to reach the COD form
     and the only route left is the /cart page — the redirect the owner explicitly does not
     want. This reveals a theme-rendered trigger ONLY when Releasit's button has not appeared,
     so the two can never both be on screen. The click itself is handled by initProduct's
     capture listener ([data-pt-cod]), i.e. the same bridge as Releasit's own button. */
  function initCodFallback(root) {
    var btn = root.querySelector("[data-pt-cod]");
    if (!btn) return null;
    var GRACE = 3500;   // Releasit's bundle is ~1.4MB; well past its arrival on a 3G-ish phone
    var WATCH = 15000;  // stop watching entirely — a button that has not injected by now never will
    var ac = makeAC();
    var obs = null;
    var pending = false;
    var revealTimer = null;
    var stopTimer = null;

    function releasitButtonPresent() {
      return !!(document.getElementById("rsi_buy_now_button") || document.querySelector("[data-rsi-cod-button]"));
    }
    function stop() {
      if (obs) { obs.disconnect(); obs = null; }
      if (revealTimer) { clearTimeout(revealTimer); revealTimer = null; }
      if (stopTimer) { clearTimeout(stopTimer); stopTimer = null; }
    }
    function evaluate() {
      pending = false;
      var present = releasitButtonPresent();
      /* Before the grace window the fallback stays hidden: showing it and then pulling it
         away the moment Releasit injects would be a visible flicker on the buy box. */
      if (present) { btn.hidden = true; stop(); return; }
      if (!revealTimer) btn.hidden = false;
    }
    /* Debounced to one check per frame: Vitals and Releasit both mutate this page heavily
       while they boot, and an un-throttled observer callback would run hundreds of times. */
    function schedule() {
      if (pending) return;
      pending = true;
      if (window.requestAnimationFrame) window.requestAnimationFrame(evaluate);
      else setTimeout(evaluate, 16);
    }

    if (releasitButtonPresent()) return null; // already there — nothing to watch or reveal

    if ("MutationObserver" in window) {
      obs = new MutationObserver(schedule);
      obs.observe(document.body, { childList: true, subtree: true });
    }
    revealTimer = setTimeout(function () { revealTimer = null; evaluate(); }, GRACE);
    stopTimer = setTimeout(stop, WATCH);

    return function teardown() { stop(); ac.abort(); };
  }

  function initLazyDesc(root) {
    var nodes = Array.prototype.slice.call(root.querySelectorAll("[data-pt-src],[data-pt-srcset]"));
    if (!nodes.length) return null;

    function restore(el) {
      var s = el.getAttribute("data-pt-src");
      var ss = el.getAttribute("data-pt-srcset");
      if (ss) { el.setAttribute("srcset", ss); el.removeAttribute("data-pt-srcset"); }
      if (s) { el.setAttribute("src", s); el.removeAttribute("data-pt-src"); }
    }

    if (!("IntersectionObserver" in window)) {
      nodes.forEach(restore);
      return null;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { restore(en.target); io.unobserve(en.target); }
      });
    }, { rootMargin: "400px 0px" });

    nodes.forEach(function (el) { io.observe(el); });
    return function teardown() { io.disconnect(); };
  }

  /* ---------- PDP mobile sticky add-to-cart bar ----------
     Shows the fixed bottom bar when the main buy form scrolls out of view.
     CTA mirrors the main buy action: prefer Releasit's own injected Buy Now
     button (relocated into [data-rsi-buynow-slot]), otherwise Add-to-cart.
     Mobile-only (CSS hides it >= 750px). No layout shift (position:fixed). */
  function initStickyAtc(root) {
    var bar = root.querySelector("[data-sticky-atc]");
    var form = root.querySelector("[data-pdp-form]");
    if (!bar || !form) return null;
    var ac = makeAC(), signal = ac.signal;
    var cta = bar.querySelector("[data-sticky-cta]");
    var priceEl = bar.querySelector("[data-sticky-price]");
    var mainPrice = root.querySelector("[data-pdp-price]");
    var ctaLabel = cta ? cta.textContent : "";
    var ctaTimer = null;
    var awaitingAdd = false;   // only confirm adds THIS bar started

    function show(v) {
      if (v) { bar.hidden = false; bar.setAttribute("aria-hidden", "false"); bar.classList.add("is-visible"); }
      else { bar.classList.remove("is-visible"); bar.setAttribute("aria-hidden", "true"); bar.hidden = true; }
    }

    // Keep sticky price in sync with the main PDP price (variant changes).
    function syncPrice() { if (priceEl && mainPrice) priceEl.textContent = mainPrice.textContent; }

    if (cta) {
      /* The main button paints "✓ Dodano do koszyka" on itself, but by the time this bar
         is on screen that button is far above the fold, so an add started here used to land
         with no visible feedback beyond the small header badge. Mirror the same success
         state onto the sticky CTA, driven by the real cart event — never optimistically,
         so it can only ever confirm an add that actually happened. */
      function flashAdded() {
        if (ctaTimer) clearTimeout(ctaTimer);
        cta.textContent = "✓ Dodano do koszyka";
        cta.classList.add("is-added");
        ctaTimer = setTimeout(function () {
          cta.textContent = ctaLabel;
          cta.classList.remove("is-added");
          ctaTimer = null;
        }, 2000);
      }
      document.addEventListener("pt:cart:updated", function () {
        if (!awaitingAdd) return;
        awaitingAdd = false;
        flashAdded();
      }, { signal: signal });

      cta.addEventListener("click", function (e) {
        e.preventDefault();
        /* 2026-09-06: the bar reads "Kup teraz" - the same words as the buy-box button that
           opens the COD form - so it must do the same thing. Route through the PDP's Releasit
           bridge (single-flight, Vitals performs the add, form opens in place). The 2026-06
           reason for demoting this to a plain add (double-tap + /cart redirect) is covered now
           by releasitBridgeBusy and ptFormOpenInFlight. Plain add remains only as the fallback
           when initProduct did not bind. */
        if (typeof root.__ptBridge === "function") { root.__ptBridge(cta); return; }
        // Sticky CTA -> same AJAX add-to-cart as the main button (Releasit-first removed: was causing double-tap + /cart redirect).
        // The submit is dispatched on the form, so the ATC button's own click listener never
        // runs: record the ADD intent here, otherwise a stale "form" intent from an earlier
        // tap could make the Vitals hook open the COD form on a plain add.
        var add = root.querySelector("[data-pdp-add]");
        if (add && add.getAttribute("aria-busy") !== "true") {
          setBuyIntent("cart");
          awaitingAdd = true;
          form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
        }
      }, { signal: signal });
    }

    var io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          syncPrice();
          // Show the sticky bar only once the buy form has scrolled UP and out of
          // view (its bottom edge above the viewport). Keep it hidden when the
          // form is in view or still below the fold (initial page load).
          var scrolledPast = !en.isIntersecting && en.boundingClientRect.top < 0;
          show(scrolledPast);
        });
      }, { threshold: 0 });
      io.observe(form);
    }

    return function teardown() { ac.abort(); if (io) io.disconnect(); if (ctaTimer) { clearTimeout(ctaTimer); ctaTimer = null; } show(false); };
  }

  /* ---------- Cart (AJAX qty / remove, debounced) ---------- */
  function initCart(root) {
    var form = root.querySelector("[data-cart-form]");
    if (!form) return null; // empty cart
    var ac = makeAC(), signal = ac.signal;
    var timers = {};

    function lineEl(t) { return t.closest("[data-line]"); }

    // Rebuild a line's whole price block (struck original / discounted now /
    // "Oszczędzasz X zł" badge) from the AJAX cart JSON so the markup structure
    // and the savings badge stay consistent after a qty change — no reload.
    // Numbers are sourced straight from the cart response (original_line_price,
    // final_line_price); the saving is original − final per the authoritative
    // cart JSON, never recomputed from a client-side discount rule. If a clean
    // positive saving can't be derived, the badge is hidden rather than stale.
    function paintLinePrice(li, item, currency) {
      var aside = li.querySelector(".pt-line__aside");
      var priceEl = li.querySelector(".pt-line__price");
      if (!aside || !priceEl) return;
      var orig = item.original_line_price;
      var fin = item.final_line_price;
      var saving = (typeof orig === "number" && typeof fin === "number") ? (orig - fin) : 0;
      var discounted = saving > 0;

      if (discounted) {
        priceEl.innerHTML =
          '<s class="pt-line__was">' + formatMoney(orig, currency) + "</s>" +
          '<span class="pt-line__now">' + formatMoney(fin, currency) + "</span>";
      } else {
        priceEl.textContent = formatMoney(fin, currency);
      }

      var saveEl = aside.querySelector(".pt-line__save");
      if (discounted) {
        if (!saveEl) {
          saveEl = document.createElement("span");
          saveEl.className = "pt-line__save";
          priceEl.insertAdjacentElement("afterend", saveEl);
        }
        saveEl.textContent = "Oszczędzasz " + formatMoney(saving, currency);
      } else if (saveEl) {
        saveEl.remove();
      }
    }

    function update(line, qty) {
      var li = root.querySelector('[data-line="' + line + '"]');
      if (li) li.setAttribute("aria-busy", "true");
      fetch("/cart/change.js", {
        method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line: Number(line), quantity: Number(qty) })
      }).then(function (r) { return r.json(); })
        .then(function (cart) {
          paintCartCount(cart.item_count);
          if (cart.item_count === 0) { window.location.reload(); return; }
          // line removed?
          if (qty === 0 && li) {
            li.remove();
            // re-index remaining lines so subsequent AJAX ops use correct line numbers (no reload)
            root.querySelectorAll("[data-line]").forEach(function (el, i) { el.setAttribute("data-line", i + 1); });
          }
          else if (li) {
            var item = cart.items[line - 1];
            if (item) paintLinePrice(li, item, cart.currency);
            li.setAttribute("aria-busy", "false");
          }
          var sub = root.querySelector("[data-cart-subtotal]"); if (sub) sub.textContent = formatMoney(cart.total_price, cart.currency);
          var tot = root.querySelector("[data-cart-total]"); if (tot) tot.textContent = formatMoney(cart.total_price, cart.currency);
          document.dispatchEvent(new CustomEvent("pt:cart:updated", { detail: cart }));
        }).catch(function () { if (li) li.setAttribute("aria-busy", "false"); });
    }
    function debouncedUpdate(line, qty) { clearTimeout(timers[line]); timers[line] = setTimeout(function () { update(line, qty); }, 350); }

    /* Same upper bound as the PDP stepper (see QTY_MAX in initProduct). The cart line input
       was min="0" with no max, so the PDP cap alone would have been trivially bypassable by
       editing the quantity here instead. 0 stays legal — it is how a line is removed. */
    var LINE_QTY_MAX = 24;
    function clampLineQty(n) {
      n = parseInt(n, 10);
      if (isNaN(n) || n < 0) n = 0;
      return Math.min(LINE_QTY_MAX, n);
    }

    root.addEventListener("click", function (e) {
      var li = lineEl(e.target); if (!li) return;
      var line = li.getAttribute("data-line");
      var input = li.querySelector("[data-line-qty]");
      if (e.target.closest("[data-line-remove]")) { update(line, 0); return; }
      /* Null-guarded: a line row without a qty input would otherwise throw on .value
         inside this handler for every click landing in that row. */
      if (!input) return;
      if (e.target.closest("[data-line-inc]")) { input.value = clampLineQty((parseInt(input.value, 10) || 0) + 1); debouncedUpdate(line, input.value); }
      if (e.target.closest("[data-line-dec]")) { input.value = clampLineQty((parseInt(input.value, 10) || 0) - 1); debouncedUpdate(line, input.value); }
    }, { signal: signal });

    root.addEventListener("change", function (e) {
      var input = e.target.closest("[data-line-qty]"); if (!input) return;
      var li = lineEl(input); if (!li) return;
      var line = li.getAttribute("data-line");
      input.value = clampLineQty(input.value);
      debouncedUpdate(line, input.value);
    }, { signal: signal });

    return function teardown() {
      ac.abort();
      Object.keys(timers).forEach(function (k) { clearTimeout(timers[k]); });
    };
  }

  /* ---------- Cart native-checkout firewall (capture phase, global) ----------
     GROUND TRUTH (live audit): Releasit on Potencium is build "releasit-cod-form-422"
     in SEPARATE-BUTTON mode. It injects its OWN button #rsi_buy_now_button into
     #cart-wrapper inside form#cart and clicking THAT opens the COD popup. There is
     nothing for the theme to "convert" and no force-show is needed.

     This firewall mirrors NightForce v8.0: a single capture-phase guard installed
     ONCE on document that GUARANTEES the theme can never navigate to native Shopify
     checkout from the cart. It intercepts, before any other handler:
       - any form submit whose resolved action points at /checkout(s)
       - any click on #checkout / [data-cart-checkout] / button[name="checkout"]
         that is NOT a Releasit-owned COD button (:not([data-rsi-cod-button]))
     On a hit it preventDefault + stopImmediatePropagation, then opens the COD popup
     by clicking the injected #rsi_buy_now_button. If that button is not present yet,
     it does NOT fall back to native checkout — it logs a soft "loading" state and
     returns. Installed once; memory-safe (no per-section listeners, never town down). */
  var _ptCheckoutFirewallInstalled = false;
  function installCheckoutFirewall() {
    if (_ptCheckoutFirewallInstalled) return;
    _ptCheckoutFirewallInstalled = true;

    // Open the Releasit COD form across BOTH modes:
    //  • PDP separate-button mode injects #rsi_buy_now_button
    //  • /cart converter mode converts the existing checkout button (it gains
    //    data-rsi-cod-button — the firewall below lets that marker through, so clicking
    //    it programmatically reaches Releasit's own handler, no loop)
    //  • some builds expose a JS API
    function releasitOpen() {
      if (window.ReleasitCOD && typeof window.ReleasitCOD.openForm === "function") { window.ReleasitCOD.openForm(); return true; }
      if (window.releasit && typeof window.releasit.openForm === "function") { window.releasit.openForm(); return true; }
      var rsi = document.querySelector("#rsi_buy_now_button, [data-rsi-cod-button]");
      if (rsi) { rsi.click(); return true; }
      return false;
    }
    function openReleasitCOD(originBtn) {
      if (releasitOpen()) return true;
      // Releasit hasn't converted the button yet — show a visible loading state on the clicked
      // control and poll until ready, then open. NEVER fall back to native checkout, and never
      // fail silently (that was the dead-click bug).
      var btn = originBtn || document.querySelector("#checkout, [data-cart-checkout]");
      var restore = null;
      if (btn && !btn.getAttribute("data-rsi-loading")) {
        var orig = btn.textContent;
        btn.setAttribute("data-rsi-loading", "1");
        btn.setAttribute("aria-busy", "true");
        btn.textContent = "Ładowanie…";
        restore = function () { btn.textContent = orig; btn.removeAttribute("aria-busy"); btn.removeAttribute("data-rsi-loading"); };
      }
      var tries = 0;
      (function poll() {
        if (releasitOpen()) { if (restore) restore(); return; }
        if (++tries > 50) { // ~10s ceiling
          if (restore) restore();
          /* Releasit is proven absent. Arm the native fallback so the firewall stands down and
             the NEXT tap performs a real form submit, instead of repeating this same 10-second
             dead wait forever. This is the only place the marker is ever set — it is a
             confirmed-absent signal, not a timeout guess. Purely behavioural: the marker is a
             data-attribute and a root class with no styling attached, so nothing moves visually. */
          try {
            if (btn && btn.setAttribute) btn.setAttribute("data-pt-native-fallback", "1");
            var de = document.documentElement;
            if (de && de.className.indexOf("pt-releasit-absent") === -1) {
              de.className += " pt-releasit-absent";
            }
          } catch (e) {}
          if (window.console && console.warn) console.warn("[pt-cart] Releasit COD nie załadował się w wyznaczonym czasie — przywracam natywną wysyłkę formularza");
          return;
        }
        setTimeout(poll, 200);
      })();
      return false;
    }

    /* ---------- native-fallback escape hatch (2026-08-10) ----------
       THE BUG THIS FIXES. The two firewall handlers below intercept every checkout control
       unconditionally (preventDefault + stopImmediatePropagation → openReleasitCOD). Meanwhile
       templates/cart.liquid ships its checkout button as type="submit" and, if Releasit has not
       appeared within 3s, promotes it back to type="submit" as an explicit dead-cart fallback.

       Those two mechanisms contradicted each other: the firewall also blocked that fallback
       submit, openReleasitCOD polled ~10s for an app that was never going to load, logged a
       warning and submitted NOTHING. So the documented fallback was real in the markup but
       neutralised at runtime, and the cart stayed dead in exactly the FB/IG in-app-browser case
       it was written for — 66% of this store's traffic.

       The rule: the firewall stands down ONLY on a POSITIVE, proven-absent signal. An ordinary
       slow Releasit load must still be routed to the COD form, otherwise shoppers leak into
       native Shopify checkout and bypass cash-on-delivery entirely — which is the very thing
       the firewall exists to prevent. Hence a dedicated marker set only after the poll is
       exhausted, never a timeout guess made here. */
    function nativeFallbackArmed(el) {
      try {
        if (el && el.getAttribute && el.getAttribute("data-pt-native-fallback") !== null) return true;
        var de = document.documentElement;
        return !!(de && de.className && de.className.indexOf("pt-releasit-absent") !== -1);
      } catch (e) { return false; }
    }

    function resolvesToCheckout(action) {
      if (!action) return false;
      try {
        var u = new URL(action, window.location.origin);
        return /\/checkouts?(\/|$|\?)/.test(u.pathname);
      } catch (e) {
        return /\/checkouts?(\/|$|\?)/.test(String(action));
      }
    }

    // Submit firewall: any cart form submit that would reach native checkout.
    document.addEventListener("submit", function (e) {
      var form = e.target;
      if (!form || form.tagName !== "FORM") return;
      // Native cart checkout is triggered either by action=/checkout or by a
      // name="checkout" submitter inside the cart form (action=/cart).
      var sub = e.submitter;
      /* Stand down once Releasit is proven absent — otherwise this blocks the cart template's
         own dead-cart fallback submit and the shopper is left with a button that does nothing. */
      if (nativeFallbackArmed(sub)) return;
      var viaSubmitter = sub && sub.name === "checkout" && !sub.hasAttribute("data-rsi-cod-button");
      if (resolvesToCheckout(form.getAttribute("action")) || viaSubmitter) {
        e.preventDefault();
        e.stopImmediatePropagation();
        openReleasitCOD();
      }
    }, true);

    // Click firewall: any click on a theme checkout control (not Releasit's own).
    document.addEventListener("click", function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var hit = t.closest('#checkout, [data-cart-checkout], button[name="checkout"]');
      if (!hit) return;
      if (hit.hasAttribute("data-rsi-cod-button")) return; // Releasit owns it — let it through
      if (hit.id === "rsi_buy_now_button") return;         // never block Releasit's button
      if (nativeFallbackArmed(hit)) return;                // Releasit proven absent — allow the native submit through
      e.preventDefault();
      e.stopImmediatePropagation();
      openReleasitCOD(hit);
    }, true);
  }

  /* ---------- Vitals Volume-Discount ↔ qty two-way sync ----------
     Waits for the Vitals widget to inject into [data-vitals-volume] (or
     anywhere in document.body) before attaching any listeners — so this
     is a PURE NO-OP when Vitals has not rendered (owner app-side issue).
     Selectors are ported verbatim from sections/main-product.liquid
     fnVitalsSync (lines 521-660) which confirmed these on the live NF DOM:
       .Vtl-NewVolumeDiscountRadio__TierLabel[role="radio"]
       .Vtl-NewVolumeDiscountRadio__TierItem--Selected .Vtl-NewVolumeDiscountRadio__TierLabel[role="radio"]
       .Vtl-NewVolumeDiscountRadio__TierLabel[aria-checked="true"]
       .Vtl-NewVolumeDiscountRadio__TierList
     Label id encodes qty as the last dash-separated segment (e.g. "…-tier-2-discount-2"). */
  function initVitalsSync(root) {
    var qtyInput = root.querySelector("[data-qty-input]");
    if (!qtyInput) return null; // no qty input → not a product page, hard bail

    var ac = makeAC();
    var signal = ac.signal;
    var syncing = false;        // true while WE are driving a tier .click() and its render cascade
    var syncSettleTimer = null; // self-extending timer that releases `syncing` once the cascade goes quiet
    var qtyToTierTimer = null;  // debounce: coalesce rapid qty taps into ONE Vitals render
    var tierToQtyTimer = null;  // debounce: collapse Vitals' many internal mutations into ONE qty reconcile
    var lastSource = null;      // "qty" | "tier": which control the shopper touched last
    function markSource(src) { lastSource = src; }
    var tierObserver = null;
    var detectObserver = null;
    var detectTimeout = null;
    var reserveTimer = null; // grace timer to collapse the CLS reserve when empty
    var wired = false; // guard: attach tier listeners only once
    var vitalsSeen = false; // did the Vitals widget ever appear?

    /* ── helpers (ported from fnVitalsSync in main-product.liquid) ── */
    function qtyFromLabelId(id) {
      var parts = (id || "").split("-");
      var n = parseInt(parts[parts.length - 1], 10);
      return (isNaN(n) || n < 1) ? null : n;
    }

    function selectedTierQty() {
      var sel = document.querySelector(
        ".Vtl-NewVolumeDiscountRadio__TierItem--Selected .Vtl-NewVolumeDiscountRadio__TierLabel[role=\"radio\"]"
      ) || document.querySelector(".Vtl-NewVolumeDiscountRadio__TierLabel[aria-checked=\"true\"]");
      return sel ? qtyFromLabelId(sel.id) : null;
    }

    /* Hold the `syncing` guard for the WHOLE Vitals render cascade. A single tier
       .click() makes Vitals re-render its widget, mutating the TierList ~10x. Each
       of those mutations would otherwise ping `tierObserver`→syncTierToQty and bounce
       back into syncQtyToTier. We keep `syncing` true and re-arm a short quiet-timer on
       every observed mutation; it only releases once the cascade has been silent for
       a beat. This collapses ~10 round-trips into ONE settle — the actual lag fix. */
    function holdSyncing() {
      syncing = true;
      if (syncSettleTimer) clearTimeout(syncSettleTimer);
      syncSettleTimer = setTimeout(function () { syncing = false; syncSettleTimer = null; }, 140);
    }
    function bumpSyncing() {
      if (!syncing) return;
      if (syncSettleTimer) clearTimeout(syncSettleTimer);
      syncSettleTimer = setTimeout(function () { syncing = false; syncSettleTimer = null; }, 140);
    }

    /* ── 2026-08-15: الكمية تُكتب قبل ما Releasit يقرا الفورم ──
       capture phase على document بيشتغل قبل الهاندلر بتاع Releasit نفسه على الزرار،
       فالفورم بيتبعت دايمًا بالكمية اللي العميل اختارها من التير. من غير ده، كليك على
       تير 3 بيسجّل خصم 3 قطع عند Vitals والأوردر بيروح بقطعة واحدة. */
    /* ── 2026-08-30: the shopper's quantity wins at buy time ──
       Replaces the old pull-the-tier-into-the-input behaviour, which fired on pointerdown of
       the buy button and silently reduced what the shopper asked for in three ways:

         1. qty -> tier is debounced 180ms. Tap "+" to 3 and hit Kup teraz inside that
            window and the widget is still on tier 2, so the input was rewritten 3 -> 2.
         2. Any quantity with NO matching tier (4, 5, 7, ...) never gets a tier, so the
            pull-back was permanent, not merely a race.
         3. When Vitals takes the buy click over it adds ITS tier quantity, so a downgraded
            input meant a downgraded cart line too — a smaller basket on every fast tap.

       Now the shopper's number wins unless the TIER is what they touched last, and we push
       qty -> tier synchronously instead of the other way round. Both pending debounces are
       flushed first so nothing lands after the COD form has read the cart. This can never
       REDUCE the quantity: with no matching tier, doQtyToTier is a no-op and the number
       simply stands. The plain add-to-cart button is in the trigger list too. */
    function settleQtyAndTier() {
      if (qtyToTierTimer) { clearTimeout(qtyToTierTimer); qtyToTierTimer = null; }
      if (tierToQtyTimer) { clearTimeout(tierToQtyTimer); tierToQtyTimer = null; }

      var tierQty = selectedTierQty();
      if (tierQty == null) return;               /* no widget / no selection: nothing to settle */
      var want = parseInt(qtyInput.value, 10);
      if (isNaN(want) || want < 1) return;
      if (tierQty === want) return;              /* already in step */

      if (lastSource === "tier") {
        /* Shopper picked a tier and the debounced tier -> qty had not landed yet. Adopt the
           tier's quantity — the one case the old code got right (2026-08-15). */
        qtyInput.value = tierQty;
        holdSyncing();
        qtyInput.dispatchEvent(new Event("input", { bubbles: true }));
        qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }
      /* Stepper, typed edit, or unknown: the shopper's number is the intent. Push it onto the
         widget so Vitals adds the right quantity. */
      doQtyToTier(want);
    }
    ["pointerdown", "mousedown", "touchstart", "click"].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (!t.closest("#rsi_buy_now_button, [data-rsi-cod-button], #buttonContainerProduct, [data-pdp-add]")) return;
        settleQtyAndTier();
      }, { capture: true, signal: signal });
    });

    /* Record stepper edits from the very first paint. The equivalent listeners in
       wireTierSync() only exist once Vitals has mounted, so a tap made while that bundle was
       still downloading left lastSource null and the buy-time settle treated the shopper's own
       quantity as stale. */
    root.addEventListener("click", function (e) {
      if (e.target && e.target.closest && e.target.closest("[data-qty-dec],[data-qty-inc]")) markSource("qty");
    }, { signal: signal });
    root.addEventListener("input", function (e) {
      if (e.target === qtyInput && !syncing) markSource("qty");
    }, { signal: signal });
    root.addEventListener("change", function (e) {
      if (e.target === qtyInput && !syncing) markSource("qty");
    }, { signal: signal });

    /* ── tier → qty (debounced; runs once per settled cascade) ── */
    function syncTierToQty() {
      if (syncing) return;
      var q = selectedTierQty();
      if (q == null) return;
      var cur = parseInt(qtyInput.value, 10);
      if (cur === q) return;
      /* updating the input value alone is enough for our PDP; do NOT re-dispatch
         input/change here — that re-fired the qty→tier path and caused the bounce. */
      qtyInput.value = q;
      /* 2026-08-15 FIX: كتابة .value لوحدها مالهاش صوت — Releasit وغيره مابيسمعوش،
         وده اللي خلّى خصم 3 قطع ينزل على أوردر بقطعة واحدة (#2731 = 0.00 PLN).
         بنطلق input/change صراحةً. مفيش bounce: doQtyToTier() بيرجع فورًا لو التير
         المختار يساوي القيمة أصلاً — وهي كده بالظبط في اللحظة دي. */
      holdSyncing();
      qtyInput.dispatchEvent(new Event("input", { bubbles: true }));
      qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
    function scheduleTierToQty() {
      if (tierToQtyTimer) clearTimeout(tierToQtyTimer);
      tierToQtyTimer = setTimeout(function () { tierToQtyTimer = null; syncTierToQty(); }, 90);
    }

    /* ── qty → tier (debounced; ONE Vitals render after the user stops tapping) ── */
    function doQtyToTier(val) {
      var q = parseInt(val, 10);
      if (isNaN(q) || q < 1) return;
      if (selectedTierQty() === q) return;
      var labels = document.querySelectorAll(".Vtl-NewVolumeDiscountRadio__TierLabel[role=\"radio\"]");
      for (var i = 0; i < labels.length; i++) {
        if (qtyFromLabelId(labels[i].id) === q) {
          holdSyncing();     // guard the whole render cascade this click triggers
          labels[i].click();
          break;
        }
      }
    }
    function scheduleQtyToTier() {
      if (qtyToTierTimer) clearTimeout(qtyToTierTimer);
      /* debounce so rapid +/- presses fire exactly ONE heavyweight Vitals render */
      qtyToTierTimer = setTimeout(function () { qtyToTierTimer = null; doQtyToTier(qtyInput.value); }, 180);
    }

    /* ── wired once Vitals renders ── */
    function wireTierSync() {
      if (wired) return;
      wired = true;

      /* tier click (real user picking a tier) → reconcile qty, debounced */
      document.addEventListener("click", function (e) {
        if (!e.target.closest(".Vtl-NewVolumeDiscountRadio")) return;
        if (syncing) return; // ignore the synthetic click WE dispatched
        markSource("tier");   // the tier is now the shopper's stated intent
        scheduleTierToQty();
      }, { signal: signal });

      /* qty +/- AND direct input/change → schedule ONE debounced tier sync.
         The qty input value itself is updated synchronously by initProduct's click
         handler, so the displayed number is already instant here; we only defer the
         (heavy) Vitals render. A single debounced path covers clicks + typing — no
         duplicate root click listener, no per-press render. */
      root.addEventListener("click", function (e) {
        if (!e.target.closest("[data-qty-dec],[data-qty-inc]")) return;
        markSource("qty");
        scheduleQtyToTier();
      }, { signal: signal });
      root.addEventListener("input", function (e) {
        if (e.target !== qtyInput) return;
        scheduleQtyToTier();
      }, { signal: signal });
      root.addEventListener("change", function (e) {
        if (e.target !== qtyInput) return;
        scheduleQtyToTier();
      }, { signal: signal });

      /* Observe TierList for attribute mutations (Vitals marks selection via class/aria).
         While WE are driving the render, every mutation just re-arms the settle timer
         (bumpSyncing) instead of bouncing back. Once quiet, a single debounced
         reconcile snaps qty to whatever tier ended up selected. */
      var tierList = document.querySelector(".Vtl-NewVolumeDiscountRadio__TierList");
      if (tierList) {
        tierObserver = new MutationObserver(function () {
          if (syncing) { bumpSyncing(); return; }
          scheduleTierToQty();
        });
        tierObserver.observe(tierList, {
          attributes: true, childList: true, subtree: true,
          attributeFilter: ["class", "aria-checked"]
        });
      }

      /* One-time: if no tier is currently selected, preselect Tier 2 (matches NF default) */
      var labels = document.querySelectorAll(".Vtl-NewVolumeDiscountRadio__TierLabel[role=\"radio\"]");
      if (labels.length && selectedTierQty() == null) {
        doQtyToTier(2);
      } else {
        syncTierToQty(); /* snap qty to whatever Vitals already selected */
      }
    }

    /* ── MutationObserver to detect when Vitals injects its widget ── */
    function hasTierControls() {
      return !!document.querySelector(".Vtl-NewVolumeDiscountRadio__TierLabel[role=\"radio\"]");
    }

    function onVitalsDetected() {
      vitalsSeen = true;
      /* The Vitals widget is present; CSS `:has(.vtl-ub-vd-widget)` has already
         collapsed `.pt-vitals-reserve` synchronously. Flag the scope as a no-op
         belt-and-braces so the reserve can never re-expand. */
      root.classList.add("is-vitals-done");
      stopDetect();
      wireTierSync();
    }

    var detectPoll = null;
    function stopDetect() {
      if (detectObserver) { detectObserver.disconnect(); detectObserver = null; }
      if (detectPoll)     { clearInterval(detectPoll);   detectPoll = null; }
      if (detectTimeout)  { clearTimeout(detectTimeout); detectTimeout = null; }
      if (reserveTimer)   { clearTimeout(reserveTimer);  reserveTimer = null; }
      /* If detection ended WITHOUT a widget, this product has no volume discount —
         collapse the CLS reserve so non-discount PDPs don't keep a blank gap. */
      if (!vitalsSeen) { root.classList.add("is-vitals-empty"); }
    }

    if (hasTierControls()) {
      /* Vitals already present at init time */
      vitalsSeen = true;
      root.classList.add("is-vitals-done");
      wireTierSync();
    } else {
      /* IMPORTANT: Vitals does NOT inject into the [data-vitals-volume] slot —
         it auto-places into its own `vtl-ub-vd-widget` node inside
         `.product__info-container`, OUTSIDE our slot (slot stays empty). So we
         MUST observe document.body, never the empty slot, or detection never
         fires and the whole sync silently no-ops. A polling fallback covers the
         race where the widget is injected in a single batch the observer misses. */
      detectObserver = new MutationObserver(function () {
        if (hasTierControls()) { onVitalsDetected(); }
      });
      detectObserver.observe(document.body, { childList: true, subtree: true });

      /* Polling fallback (cheap selector check) — catches missed-mutation races */
      detectPoll = setInterval(function () {
        if (hasTierControls()) { onVitalsDetected(); }
      }, 400);

      /* Hard cap: stop detecting after 20s — Vitals didn't render, stay no-op */
      detectTimeout = setTimeout(stopDetect, 20000);

      /* CLS reserve grace: Vitals reliably mounts within a couple of seconds. If no widget
         has appeared by then, collapse the reserve so non-discount PDPs don't sit on a
         blank gap for the full 20s detect window. (Discount PDPs collapse the reserve
         synchronously via `:has()` the moment the widget mounts, well before this fires.)
         2026-08-30: was 5000ms. Measured on the live PDP, that left a 380px empty block
         above the buy button for five seconds and then jumped the whole buy box upward —
         right in the window where a shopper reaches for the button. Detection itself still
         runs for the full 20s, so a very late widget is still wired up; only the blank
         placeholder gives up early. */
      reserveTimer = setTimeout(function () {
        if (!vitalsSeen) { root.classList.add("is-vitals-empty"); }
      }, 2500);
    }

    return function teardown() {
      ac.abort();
      stopDetect();
      if (tierObserver) { tierObserver.disconnect(); tierObserver = null; }
      if (syncSettleTimer) { clearTimeout(syncSettleTimer); syncSettleTimer = null; }
      if (qtyToTierTimer)  { clearTimeout(qtyToTierTimer);  qtyToTierTimer = null; }
      if (tierToQtyTimer)  { clearTimeout(tierToQtyTimer);  tierToQtyTimer = null; }
      syncing = false;
    };
  }

  /* ---------- registry / lifecycle ---------- */
  /* ---------- Releasit: removing the LAST line closes the form ----------
     Releasit ships a per-line remove button but swaps its styled-component class to a hidden
     variant once a single line is left (hErSXU -> kmXPKL). Verified live 2026-09-06 why: force
     that button visible and click it, and the cart empties while the form stays open with a
     live "ZLOZ ZAMOWIENIE 0,00 zl" button - a submittable empty COD order, which on a
     cash-on-delivery store is a real shipping cost against nothing.

     custom.css force-shows the button on every line; this makes the last one behave as cancel.
     We let Releasit run its own removal and cart write first, then click Releasit's own close
     control so its internal state unwinds the way it expects rather than calling dialog.close()
     behind its back. */
  function initRsiLastLineClose() {
    var ac = makeAC(), signal = ac.signal;
    document.addEventListener("click", function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var btn = t.closest(".rsi-product-item-remove");
      if (!btn) return;
      var dlg = btn.closest("dialog");
      if (!dlg) return;
      /* Count names, not wrappers: after a removal Releasit leaves the empty product
         wrapper element standing (verified live 2026-09-06 - the wrapper count stayed 1 while
         the name count dropped to 0), so a wrapper-based poll never reaches zero. */
      if (dlg.querySelectorAll(".rsi-product-item-name").length > 1) return;

      /* Close first, reconcile after. Waiting for Releasit to finish its own removal put a
         skeleton loader in front of the shopper for a second or more on what is, to them,
         simply "cancel". Releasit's handler still runs - we never preventDefault - and the
         reconcile below covers the one risk of closing early: the dialog unmounting with its
         cart request still in flight. */
      var card = btn.closest(".rsi-product-item-wrapper");
      var nameEl = card && card.querySelector(".rsi-product-item-name");
      var title = nameEl ? nameEl.textContent.replace(/^\s+|\s+$/g, "") : "";
      var closeBtn = dlg.querySelector(".rsi-form-close-button");
      if (closeBtn) { closeBtn.click(); } else { try { dlg.close(); } catch (err) {} }

      setTimeout(function () {
        fetch("/cart.js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var line = null, items = cart.items || [];
            for (var i = 0; i < items.length; i++) {
              var t = String(items[i].product_title || items[i].title || "");
              if (title && t.indexOf(title) === 0) { line = items[i]; break; }
            }
            if (!line) return null;   /* Releasit already removed it - nothing to do */
            return fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: line.key, quantity: 0 })
            });
          })
          .then(function () {
            if (document.querySelector("[data-cart]")) { window.location.reload(); return; }
            refreshCart();
          })
          .catch(function () {});
      }, 700);
    }, { capture: true, signal: signal });
    return function teardown() { ac.abort(); };
  }

  /* ---------- listing auto-load ([data-autoload]) ----------
     Replaces the numbered pager on the collection listing: a sentinel below
     the grid pulls the next page in and appends its cards.

     Why appending is safe: the cards are the same server-rendered fn-pg-card
     markup, and every card behaviour on this page is delegated at the .fn-pg
     root (initQuickAdd), so new nodes are live the moment they land — no
     re-init, no listener leak. Quantities come from the server with the cart
     already applied, so an appended card that is in the cart arrives green.

     The pager is hidden rather than removed, and comes back on failure. */
  function initCollectionAutoLoad(root) {
    var grid = root.querySelector("[data-autoload-grid]");
    if (!grid || !("IntersectionObserver" in window)) return;

    var pager = document.querySelector("[data-autoload-fallback]");
    var next = root.getAttribute("data-next-url");
    if (!next) return;
    if (pager) pager.hidden = true;

    var ac = makeAC(), signal = ac.signal;
    var busy = false, io = null;

    var sentinel = document.createElement("div");
    sentinel.className = "pt-autoload__sentinel";
    sentinel.setAttribute("aria-hidden", "true");
    grid.parentNode.insertBefore(sentinel, grid.nextSibling);

    var status = document.createElement("p");
    status.className = "pt-autoload__status";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    grid.parentNode.insertBefore(status, sentinel);

    function stop(restorePager) {
      if (io) { io.disconnect(); io = null; }
      sentinel.remove();
      status.remove();
      if (restorePager && pager) pager.hidden = false;
    }

    function load() {
      if (busy || !next) return;
      busy = true;
      status.textContent = "Ładowanie…";
      var url = next;
      fetch(url, { headers: { Accept: "text/html" }, signal: signal })
        .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var nextGrid = doc.querySelector("[data-autoload-grid]");
          var nextRoot = doc.querySelector("[data-autoload]");
          if (!nextGrid) throw new Error("no grid");

          var frag = document.createDocumentFragment();
          while (nextGrid.firstElementChild) frag.appendChild(nextGrid.firstElementChild);
          grid.appendChild(frag);

          // Deliberately NOT rewriting the address bar. Doing so parked the URL
          // on ?page=2, so a refresh reloaded a page holding only the last 8
          // products — with the numbered pager back on screen, because that
          // page has no "next" for the loader to hide it against. Leaving the
          // URL on page 1 means a refresh restarts the same continuous list.

          next = nextRoot ? nextRoot.getAttribute("data-next-url") : null;
          busy = false;
          status.textContent = "";
          if (!next) stop(false);
        })
        .catch(function (err) {
          if (err && err.name === "AbortError") return;
          if (window.console && console.warn) console.warn("[pt-autoload]", err);
          // Hand the shopper back the numbered pager rather than a dead end.
          stop(true);
        });
    }

    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) load();
    }, { rootMargin: "700px 0px" });
    io.observe(sentinel);

    return function teardown() { ac.abort(); stop(false); };
  }

  var INITS = [
    { sel: ".pt-announce", fn: initAnnounce },
    { sel: ".pt-header",   fn: initHeader },
    { sel: "[data-atc-scope]", fn: initAtc },
    { sel: ".fn-pg", fn: initQuickAdd },
    { sel: "[data-autoload]", fn: initCollectionAutoLoad },
    { sel: "[data-auto-carousel]", fn: initAutoCarousel },
    { sel: ".fn-spotlight", fn: initQuickAdd },
    { sel: "[data-testimonials]", fn: initTestimonials },
    { sel: "[data-gallery]", fn: initGallery },
    { sel: "[data-product]", fn: initProduct },
    { sel: "[data-product]", fn: initPdpGallery },
    { sel: "[data-product]", fn: initPdpLightbox },
    { sel: "[data-product]", fn: initReleasitSlot },
    { sel: "[data-lazy-desc]", fn: initLazyDesc },
    { sel: "[data-product]", fn: initStickyAtc },
    { sel: "[data-product]", fn: initCodFallback },
    { sel: "[data-product]", fn: initVitalsSync },
    { sel: "[data-cart]", fn: initCart },
    { sel: "body", fn: initRsiLastLineClose }
  ];

  function boot(scope) {
    INITS.forEach(function (entry) {
      // ISOLATION: each initializer runs in its own try/catch so a single failing
      // init can NEVER prevent the others from binding. Before this guard, an
      // uncaught throw in any one init aborted the whole forEach — which would
      // silently kill every later initializer, including the ATC binders. A caught
      // failure is logged (console.warn) and skipped; the rest still bind.
      var nodes;
      try { nodes = (scope || document).querySelectorAll(entry.sel); }
      catch (e) { if (window.console && console.warn) console.warn("[pt] selector failed:", entry.sel, e); return; }
      nodes.forEach(function (el) {
        // Allow multiple controllers per element: track which init fns are bound.
        if (!el.__ptBound) el.__ptBound = {};
        var key = entry.fn.name || entry.sel;
        if (el.__ptBound[key]) return;
        var teardown;
        try {
          teardown = entry.fn(el);
        } catch (e) {
          if (window.console && console.warn) console.warn("[pt] init failed (others unaffected):", key, e);
          return; // skip only this init on this element; never break the loop
        }
        if (teardown) { el.__ptBound[key] = true; controllers.push({ el: el, key: key, teardown: teardown }); }
      });
    });
  }

  function teardownWithin(container) {
    controllers = controllers.filter(function (c) {
      if (container.contains(c.el)) {
        try { c.teardown(); } catch (e) {}
        if (c.el.__ptBound) c.el.__ptBound[c.key] = false;
        return false;
      }
      return true;
    });
  }

  // Install the global cart→native-checkout firewall as early as possible
  // (capture phase, document-level, idempotent) so it is armed before Releasit
  // or any cart interaction can occur.
  installCheckoutFirewall();

  /* Install the Vitals ATC hooks before Vitals' bundle can run its first add, so a tier
     add stays on this page instead of navigating to /cart. */
  armVitalsHooks();

  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", function () { boot(); });

  /* Shopify theme-editor lifecycle */
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
  document.addEventListener("shopify:section:unload", function (e) { teardownWithin(e.target); });
})();
